import { googleStaffAuth } from '../utils/googleStaffAuth';
import { sessionClaims } from '../utils/session';
import { useMediaQuery } from '../hooks/useMediaQuery';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, getAuthHeaders } from '../config/api';
import { type Appointment, type AppointmentStatus, type CalendarEvent, type Doctor, type Patient, type TreatmentInProgress, type IntakeConversionContext, type Laboratory, type TreatmentTemplate, ROOM_COLORS } from '../components/calendar/types';
import { getWeekDays, formatTime, getZonedDate, makeDateInTimeZone, getAutoAssignedRoom, getInclusiveDateRange, normalizeDateRange, startOfDay, endOfDay, isCanceledOrNoShowAppointment, getMonthGrid } from '../components/calendar/utils';
import { notifyDragEnd, swallowNextClick } from '../components/calendar/dragClickGuard';
import { MiniMonth } from '../components/calendar/MiniMonth';
import { TimeGutter } from '../components/calendar/TimeGutter';
import { DayColumn } from '../components/calendar/DayColumn';
import { StandByArea } from '../components/calendar/StandByArea';
import { AppointmentModal, AppointmentFormState } from '../components/calendar/AppointmentModal';
import { DEFAULT_APPOINTMENT_REASON_VALUE, isDefaultAppointmentReasonName } from '../components/calendar/appointmentReason';
import { parseTreatedTeeth } from '../components/calendar/ToothSelector';
import { isNewPatientStatus } from '../components/calendar/reservationFee';
import { EventModal, EventFormState } from '../components/calendar/EventModal';
import { CalendarToolbar } from '../components/calendar/CalendarToolbar';
import { CalendarSearchResults } from '../components/calendar/CalendarSearchResults';
import { CalendarContextMenu, type CalendarContextMenuState } from '../components/calendar/CalendarContextMenu';
import { formatDate } from '../utils/dateFormat';
import { getCachedWeekAppointments, cacheWeekAppointments, invalidateAppointmentCache, isCacheVeryFresh } from '../utils/appointmentCache';
import { getCachedPatients, setCachedPatients, clearPatientsCache, getCachedDoctors, setCachedDoctors, getCachedLaboratories, setCachedLaboratories } from '../utils/referenceDataCache';
import { downloadGoogleCalendarWeek, uploadGoogleCalendarWeek, type GoogleCalendarPushResult } from '../utils/googleCalendarTransfer';
import { getEffectiveHc } from '../utils/patientHc';
import { repairTextEncoding } from '../utils/textEncoding';
import { useTheme } from '../theme/ThemeContext';
import { useSwipeNavigation } from '../hooks/useSwipeNavigation';
import { PhoneCalendarHeader } from '../components/calendar/mobile/PhoneCalendarHeader';
import { PhoneCalendarSheet, type PhoneCalendarView } from '../components/calendar/mobile/PhoneCalendarSheet';
import { PhoneDayHeader } from '../components/calendar/mobile/PhoneDayHeader';
import { PhoneScheduleView } from '../components/calendar/mobile/PhoneScheduleView';
import { PhoneMonthView } from '../components/calendar/mobile/PhoneMonthView';
import { PhoneFab } from '../components/calendar/mobile/PhoneFab';

type HistoryAction =
  | { type: 'MOVE_APPOINTMENT'; id: number; previousStart: string | null; previousEnd: string | null; previousRoom: number | null }
  | { type: 'MOVE_EVENT'; id: number; previousStart: string; previousEnd: string }
  | { type: 'RESIZE_APPOINTMENT'; id: number; previousStart: string; previousEnd: string }
  | { type: 'RESIZE_EVENT'; id: number; previousStart: string; previousEnd: string }
  | { type: 'EDIT_APPOINTMENT'; id: number; previousData: Appointment }
  | { type: 'CREATE_APPOINTMENT'; id: number }
  | { type: 'DELETE_APPOINTMENT'; data: Appointment }
  | { type: 'EDIT_EVENT'; id: number; previousData: CalendarEvent }
  | { type: 'CREATE_EVENT'; id: number }
  | { type: 'DELETE_EVENT'; data: CalendarEvent };

interface CalendarProps {
  token: string;
  initialNavigationState: CalendarNavigationState;
  onNavigationStateChange: (state: CalendarNavigationState) => void;
  onOpenTreatmentWorkspace?: (treatmentId: number, patientId?: number | null, appointmentId?: number | null) => void;
  onOpenPatientProfile?: (patientId: number) => void;
  onConvertIntake?: (context: IntakeConversionContext) => void;
  pendingIntakeConversion?: { intake: IntakeConversionContext; patient: Patient } | null;
  onClearPendingIntake?: () => void;
  onOpenMenu?: () => void;
}

export type CalendarView = 'day' | 'week' | PhoneCalendarView;

export interface CalendarNavigationState {
  currentDate: Date;
  view: CalendarView;
}

const PHONE_VIEW_STORAGE_KEY = 'dentec:calendarPhoneView';
const PHONE_VIEWS: readonly PhoneCalendarView[] = ['schedule', 'day', 'threeDay', 'month'];
const SCHEDULE_PAGE_DAYS = 30;
const SCHEDULE_MAX_DAYS = 180;

export const readStoredPhoneCalendarView = (): CalendarView => {
  try {
    const stored = localStorage.getItem(PHONE_VIEW_STORAGE_KEY);
    if (stored && (PHONE_VIEWS as readonly string[]).includes(stored)) return stored as PhoneCalendarView;
  } catch {}
  return 'day';
};

const storePhoneCalendarView = (view: CalendarView) => {
  try { localStorage.setItem(PHONE_VIEW_STORAGE_KEY, view); } catch {}
};

// Phones use schedule/day/3-day/month; desktop uses day/week.
const normalizeViewForDevice = (view: CalendarView, isPhone: boolean): CalendarView => {
  if (isPhone) return view === 'week' ? 'threeDay' : view;
  return view === 'day' || view === 'week' ? view : 'week';
};

type DateRange = { start: Date; end: Date };

const calendarContainerStyle: React.CSSProperties = {
  // maxWidth: '1800px', // Removed to fill width
  // minWidth: '1200px',
  // margin: '0 auto',
  padding: 0, // Removed padding
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',

  overflow: 'hidden',
  fontSize: '1.1rem',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column'
};

const buttonStyle: React.CSSProperties = {
  padding: '12px 24px',
  background: '#667eea',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500'
};

const pad2 = (value: number) => value.toString().padStart(2, '0');
const DEFAULT_EVENT_TITLE = 'No Agendar';

const toLaPazDateInput = (date: Date) => {
  const zoned = getZonedDate(date);
  return `${zoned.getFullYear()}-${pad2(zoned.getMonth() + 1)}-${pad2(zoned.getDate())}`;
};

const toLaPazTimeInput = (date: Date) => {
  const zoned = getZonedDate(date);
  return `${pad2(zoned.getHours())}:${pad2(zoned.getMinutes())}`;
};

const parseLaPazDateTime = (dateText: string, timeText: string): Date | null => {
  const dateParts = dateText.split('-').map(Number);
  const timeParts = timeText.split(':').map(Number);
  if (dateParts.length !== 3 || timeParts.length < 2) return null;

  const [year, month, day] = dateParts;
  const [hour, minute] = timeParts;

  if (![year, month, day, hour, minute].every(Number.isFinite)) return null;

  const dayAnchor = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return makeDateInTimeZone(dayAnchor, hour, minute);
};

const readApiError = async (response: Response, fallback: string) => {
  const text = await response.text();
  const prefix = `HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`;
  if (!text) return `${prefix}: ${fallback}`;
  try {
    const parsed = JSON.parse(text);
    return `${prefix}: ${parsed.error || parsed.message || parsed.detail || fallback}`;
  } catch {
    return `${prefix}: ${text}`;
  }
};

export default function Calendar({ token, initialNavigationState, onNavigationStateChange, onOpenTreatmentWorkspace, onOpenPatientProfile, onConvertIntake, pendingIntakeConversion, onClearPendingIntake, onOpenMenu }: CalendarProps) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the stored session changes together with the token
  const tokenClaims = useMemo(() => sessionClaims(), [token]);
  const canManageGoogle = tokenClaims.role === 'Admin';
  const { t, i18n } = useTranslation();
  const { tokens } = useTheme();
  const calendarChrome = tokens.calendar;
  const calendarButtonStyle = useMemo<React.CSSProperties>(() => ({
    ...buttonStyle,
    background: tokens.app.primary,
    color: tokens.app.primaryText
  }), [tokens.app.primary, tokens.app.primaryText]);
  const canEditAppointments = String(tokenClaims.CanEditAppointments ?? tokenClaims.canEditAppointments ?? '').toLowerCase() === 'true';
  const canEditPayments = String(tokenClaims.CanEditPayments ?? tokenClaims.canEditPayments ?? '').toLowerCase() === 'true';
  const canViewPayments = canEditPayments || String(tokenClaims.CanViewPayments ?? tokenClaims.canViewPayments ?? '').toLowerCase() === 'true';
  const [currentDate, setCurrentDate] = useState(() => new Date(initialNavigationState.currentDate));
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const d = new Date(initialNavigationState.currentDate);
    d.setDate(1);
    return d;
  });
  const [showMini, setShowMini] = useState(false);
  const isPhone = useMediaQuery('(max-width: 767px)');
  const [phonePickerOpen, setPhonePickerOpen] = useState(false);
  const [phoneSearchOpen, setPhoneSearchOpen] = useState(false);
  const [phoneSheetOpen, setPhoneSheetOpen] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'next' | 'prev' | null>(null);
  const [scheduleSpanDays, setScheduleSpanDays] = useState(SCHEDULE_PAGE_DAYS);
  const swipeAreaRef = useRef<HTMLDivElement>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [hiddenDoctorIds] = useState<Set<number>>(() => {
    try {
      const stored = localStorage.getItem('dentec-hidden-doctors');
      return stored ? new Set<number>(JSON.parse(stored)) : new Set<number>();
    } catch { return new Set<number>(); }
  });
  const visibleDoctors = useMemo(() => doctors.filter(d => !hiddenDoctorIds.has(d.id)), [doctors, hiddenDoctorIds]);
  const [laboratories, setLaboratories] = useState<Laboratory[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [form, setForm] = useState<AppointmentFormState>({
    patientId: '',
    doctorId: '',
    secondDoctorId: '',
    date: '',
    startTime: '',
    endTime: '',
    duration: '15',
    notes: '',
    color: '#667eea',
    marginColor: '',
    room: '',
    amountPaid: '',
    paymentMethod: '',
    treatmentId: '',
    treatmentIds: [],
    treatmentTemplateId: DEFAULT_APPOINTMENT_REASON_VALUE,
    treatmentTemplateSelections: [],
    customReason: t('calendar.modal.defaultConsultationReason', 'Consultation'),
    newTreatmentName: '',
    newTreatmentCost: '',
    newPatientName: '',
    newPatientContact: '',
    newPatientNotes: '',
    isNoShow: false,
    status: '',
    isAllDay: false,
    chargeAmount: '',
    reservationFeeAmount: '',
    reservationFeePaid: false,
    reservationFeePaymentMethod: 'Cash',
    treatedTeeth: []
  });
  const [eventForm, setEventForm] = useState<EventFormState>({ doctorId: '', date: '', startTime: '09:00', endTime: '10:00', title: DEFAULT_EVENT_TITLE, notes: '', recurrenceRule: '', recurrenceEndDate: '' });
  type ResizeState = { item: Appointment | CalendarEvent; type: 'appointment' | 'event'; edge: 'top' | 'bottom'; initialStart: Date; initialEnd: Date; columnIndex: number; lastDelta: number };
  const [resizing, setResizing] = useState<ResizeState | null>(null);
  const resizingRef = useRef<ResizeState | null>(null);
  useEffect(() => { resizingRef.current = resizing; }, [resizing]);
  const [patientTreatments, setPatientTreatments] = useState<TreatmentInProgress[]>([]);
  const [treatmentsLoading, setTreatmentsLoading] = useState(false);
  const [patientTreatmentsError, setPatientTreatmentsError] = useState<string | null>(null);
  const [treatmentTemplates, setTreatmentTemplates] = useState<TreatmentTemplate[]>([]);
  const [treatmentTemplatesLoading, setTreatmentTemplatesLoading] = useState(false);
  const [treatmentTemplatesError, setTreatmentTemplatesError] = useState<string | null>(null);
  const [prospectEventToConvert, setProspectEventToConvert] = useState<number | null>(null);
  const [history, setHistory] = useState<HistoryAction[]>([]);
  const [calendarTransfer, setCalendarTransfer] = useState<'upload' | 'download' | null>(null);
  const [calendarEmail, setCalendarEmail] = useState<string | null>(null);
  const [selectedCalendarId, setSelectedCalendarId] = useState<string | null>(null);
  const [selectedCalendarName, setSelectedCalendarName] = useState<string | null>(null);
  const [calendarList, setCalendarList] = useState<{ id: string; name: string; isPrimary: boolean }[]>([]);
  const [showCalendarPicker, setShowCalendarPicker] = useState(false);

  // Cache flags to prevent unnecessary refetches
  const [, setDoctorsCached] = useState(false);
  const [, setPatientsCached] = useState(false);
  const [, setLaboratoriesCached] = useState(false);
  const [eventsCached, setEventsCached] = useState(false);

  // Context Menu State
  const [contextMenu, setContextMenu] = useState<CalendarContextMenuState | null>(null);

  const handleAppointmentContextMenu = useCallback((app: Appointment, e: React.MouseEvent) => {
    e.preventDefault();
    const menuHeight = 37 + (doctors.length + 1) * 30 + 41;
    const menuWidth = 160;
    const x = e.clientX + menuWidth > window.innerWidth ? e.clientX - menuWidth : e.clientX;
    const y = e.clientY + menuHeight > window.innerHeight ? e.clientY - menuHeight : e.clientY;
    setContextMenu({ x, y, type: 'appointment', itemId: app.id });
  }, [doctors]);

  const handleEventContextMenu = useCallback((ev: CalendarEvent, e: React.MouseEvent) => {
    e.preventDefault();
    const menuHeight = 37 + (doctors.length + 1) * 30;
    const menuWidth = 160;
    const x = e.clientX + menuWidth > window.innerWidth ? e.clientX - menuWidth : e.clientX;
    const y = e.clientY + menuHeight > window.innerHeight ? e.clientY - menuHeight : e.clientY;
    setContextMenu({ x, y, type: 'event', itemId: ev.id });
  }, [doctors]);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const handleQuickDoctorChange = async (doctorId: number | null) => {
    if (!contextMenu) return;
    const { type, itemId } = contextMenu;
    closeContextMenu();

    try {
      if (type === 'appointment') {
        const appt = appointments.find(a => a.id === itemId);
        if (!appt) return;
        
        setHistory(prev => [...prev, { type: 'EDIT_APPOINTMENT', id: appt.id, previousData: appt }]);

        const payload = {
          patientId: appt.patientId,
          doctorId: doctorId,
          startTime: appt.startTime,
          endTime: appt.endTime,
          duration: appt.duration,
          notes: appt.notes ?? '',
          color: appt.color,
          marginColor: appt.marginColor ?? null,
          isNoShow: !!appt.isNoShow,
          treatmentId: appt.treatmentId,
          room: appt.room
        };
        
        const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${itemId}`), {
          method: 'PUT',
          headers: getAuthHeaders(token),
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Failed to update appointment doctor');
        invalidateAppointmentCache();
        fetchAppointments({ force: true });
      } else {
        const ev = events.find(e => e.id === itemId);
        if (!ev) return;
        
        setHistory(prev => [...prev, { type: 'EDIT_EVENT', id: ev.id, previousData: ev }]);

        const payload: any = {
          doctorId: doctorId,
          startTime: ev.startTime,
          endTime: ev.endTime,
          title: ev.title,
          notes: ev.notes
        };
        
        if (ev.isNewPatientIntake) {
          payload.isNewPatientIntake = true;
          payload.prospectName = ev.prospectName ?? null;
          payload.prospectContact = ev.prospectContact ?? null;
          payload.prospectNotes = ev.prospectNotes ?? null;
        }

        const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.EVENTS}/${itemId}`), {
          method: 'PUT',
          headers: getAuthHeaders(token),
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Failed to update event doctor');
        fetchEvents();
      }
    } catch (err) {
      console.error('Quick doctor change failed', err);
    }
  };

  const handleContextDeleteAppointment = async () => {
    if (!contextMenu || contextMenu.type !== 'appointment') return;

    const appointmentToDelete = appointments.find(a => a.id === contextMenu.itemId);
    closeContextMenu();
    if (!appointmentToDelete) return;

    if (!window.confirm(t('appointments.deleteConfirm'))) return;

    setHistory(prev => [...prev, { type: 'DELETE_APPOINTMENT', data: appointmentToDelete }]);

    try {
      const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${appointmentToDelete.id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      if (!res.ok) throw new Error('Failed to delete appointment');
      invalidateAppointmentCache();
      fetchAppointments({ force: true });
    } catch (err) {
      console.error('Failed to delete appointment:', err);
    }
  };

  const handleContextOpenTreatmentWorkspace = () => {
    if (!contextMenu || contextMenu.type !== 'appointment' || !onOpenTreatmentWorkspace) return;

    const appointment = displayAppointments.find(a => a.id === contextMenu.itemId);
    closeContextMenu();
    if (!appointment) return;

    const resolvedTreatmentId = typeof appointment.treatmentId === 'number'
      ? appointment.treatmentId
      : (typeof appointment.treatment?.id === 'number' ? appointment.treatment.id : null);

    if (!resolvedTreatmentId || typeof appointment.patientId !== 'number' || appointment.patientId <= 0) {
      return;
    }

    onOpenTreatmentWorkspace(resolvedTreatmentId, appointment.patientId, appointment.id);
  };

  // Navigation & Filters
  const [view, setView] = useState<CalendarView>(() => normalizeViewForDevice(initialNavigationState.view, isPhone));
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(null);

  useEffect(() => {
    setView(current => normalizeViewForDevice(current, isPhone));
    if (!isPhone) {
      setPhonePickerOpen(false);
      setPhoneSearchOpen(false);
      setPhoneSheetOpen(false);
    }
  }, [isPhone]);

  useEffect(() => {
    if (isPhone) storePhoneCalendarView(view);
  }, [isPhone, view]);

  useEffect(() => {
    setScheduleSpanDays(SCHEDULE_PAGE_DAYS);
  }, [view, currentDate]);

  useEffect(() => {
    onNavigationStateChange({ currentDate, view });
  }, [currentDate, onNavigationStateChange, view]);
  const [filterDoctorId, setFilterDoctorId] = useState<string>('');
  const [filterDoctorId2, setFilterDoctorId2] = useState<string>('');
  const [filterRoom, setFilterRoom] = useState<string>('');
  const [showCanceledOrNoShow, setShowCanceledOrNoShow] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [globalSearchResults, setGlobalSearchResults] = useState<Appointment[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const visibleDateRange = useMemo(() => {
    if (view === 'day') {
      return { start: startOfDay(currentDate), end: endOfDay(currentDate) };
    }

    if (view === 'threeDay' || view === 'schedule') {
      const last = new Date(currentDate);
      last.setDate(last.getDate() + (view === 'threeDay' ? 2 : scheduleSpanDays - 1));
      return { start: startOfDay(currentDate), end: endOfDay(last) };
    }

    if (view === 'month') {
      const weeks = getMonthGrid(currentDate);
      const lastWeek = weeks[weeks.length - 1];
      return { start: startOfDay(weeks[0][0]), end: endOfDay(lastWeek[lastWeek.length - 1]) };
    }

    if (selectedDateRange) {
      return normalizeDateRange(selectedDateRange.start, selectedDateRange.end);
    }

    const days = getWeekDays(currentDate);
    return { start: startOfDay(days[0]), end: endOfDay(days[days.length - 1]) };
  }, [currentDate, scheduleSpanDays, selectedDateRange, view]);

  const summarizeTreatmentFromAppointment = useCallback((treatment: any): TreatmentInProgress | null => {
    if (!treatment || typeof treatment.id !== 'number') return null;
    const cost = typeof treatment.cost === 'number' ? treatment.cost : 0;
    const paid = Array.isArray(treatment.payments)
      ? treatment.payments.reduce((sum: number, p: any) => sum + (typeof p?.amount === 'number' ? p.amount : 0), 0)
      : 0;
    const due = Math.max(0, cost - paid);
    let nextDueDate: string | null = null;
    if (Array.isArray(treatment.installments)) {
      const upcoming = treatment.installments
        .filter((ins: any) => ins && !ins.paid && ins.dueDate)
        .sort((a: any, b: any) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())[0];
      if (upcoming && upcoming.dueDate) {
        nextDueDate = upcoming.dueDate;
      }
    }
    return {
      id: treatment.id,
      name: typeof treatment.name === 'string' ? treatment.name : `Treatment ${treatment.id}`,
      cost,
      paid,
      due,
      createdAt: typeof treatment.createdAt === 'string' ? treatment.createdAt : null,
      nextDueDate
    };
  }, []);

  useEffect(() => {
    if (!showModal) {
      setPatientTreatments([]);
      setPatientTreatmentsError(null);
      setTreatmentsLoading(false);
      return;
    }

    if (!form.patientId || form.patientId === '__new__') {
      setPatientTreatments([]);
      setPatientTreatmentsError(null);
      setTreatmentsLoading(false);
      setForm(prev => ({
        ...prev,
        treatmentId: '',
        treatmentIds: [],
        treatmentTemplateId: DEFAULT_APPOINTMENT_REASON_VALUE,
        treatmentTemplateSelections: [],
        customReason: t('calendar.modal.defaultConsultationReason', 'Consultation'),
        newTreatmentName: '',
        newTreatmentCost: ''
      }));
      return;
    }

    const numericId = parseInt(form.patientId, 10);
    if (!Number.isFinite(numericId) || numericId <= 0) {
      setPatientTreatments([]);
      setPatientTreatmentsError(null);
      setTreatmentsLoading(false);
      setForm(prev => ({
        ...prev,
        treatmentId: '',
        treatmentIds: [],
        treatmentTemplateId: DEFAULT_APPOINTMENT_REASON_VALUE,
        treatmentTemplateSelections: [],
        customReason: t('calendar.modal.defaultConsultationReason', 'Consultation'),
        newTreatmentName: '',
        newTreatmentCost: ''
      }));
      return;
    }

    let cancelled = false;
    setTreatmentsLoading(true);
    setPatientTreatmentsError(null);

    const loadTreatments = async () => {
      try {
        const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENT_TREATMENTS_IN_PROGRESS, { patientId: numericId }), {
          headers: getAuthHeaders(token)
        });
        if (!res.ok) throw new Error('Failed to load treatments');
        const data: TreatmentInProgress[] = await res.json();

        let merged = [...data];
        if (editingAppointment && editingAppointment.patientId === numericId) {
          const linkedTreatments = editingAppointment.treatments?.length
            ? editingAppointment.treatments
            : editingAppointment.treatment
              ? [editingAppointment.treatment]
              : [];
          for (const linkedTreatment of linkedTreatments) {
            const extra = summarizeTreatmentFromAppointment(linkedTreatment);
            if (extra && !merged.some(m => m.id === extra.id)) {
              merged.push(extra);
            }
          }
        }

        if (!cancelled) {
          setPatientTreatments(merged);
          setForm(prev => {
            const currentIds = prev.treatmentIds ?? (prev.treatmentId ? [prev.treatmentId] : []);
            const validIds = currentIds.filter(id => merged.some(t => String(t.id) === id));
            return {
              ...prev,
              treatmentId: validIds[0] || '',
              treatmentIds: validIds
            };
          });
        }
      } catch (err: any) {
        if (!cancelled) {
          setPatientTreatments([]);
          setPatientTreatmentsError(err?.message || 'Failed to load treatments');
          setForm(prev => ({ ...prev, treatmentId: '', treatmentIds: [] }));
        }
      } finally {
        if (!cancelled) setTreatmentsLoading(false);
      }
    };

    loadTreatments();
    return () => {
      cancelled = true;
    };
  }, [editingAppointment, form.patientId, showModal, summarizeTreatmentFromAppointment, t, token]);

  useEffect(() => {
    let cancelled = false;

    const loadTemplates = async () => {
      setTreatmentTemplatesLoading(true);
      setTreatmentTemplatesError(null);
      try {
        const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.TREATMENT_TEMPLATES), {
          headers: getAuthHeaders(token)
        });
        if (!res.ok) throw new Error('Failed to load treatments');
        const data = await res.json();
        if (!cancelled) {
          setTreatmentTemplates(Array.isArray(data) ? data : []);
        }
      } catch (err: any) {
        if (!cancelled) {
          setTreatmentTemplates([]);
          setTreatmentTemplatesError(err?.message || 'Failed to load treatments');
        }
      } finally {
        if (!cancelled) setTreatmentTemplatesLoading(false);
      }
    };

    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const prospectAppointments = useMemo(() => events
    .filter(ev => ev.isNewPatientIntake)
    .map(ev => ({
      id: -100000 - ev.id,
      patientId: -100000 - ev.id,
      notes: ev.notes || '',
      startTime: ev.startTime,
      endTime: ev.endTime,
      doctorId: ev.doctorId ?? null,
      color: '#fde68a',
      marginColor: '#f59e0b',
      isProspect: true,
      prospectEventId: ev.id,
      prospectName: ev.prospectName ?? null,
      prospectContact: ev.prospectContact ?? null,
      isNoShow: false,
      treatmentId: null,
      treatment: null,
      room: null
    })), [events]);

  const augmentedPatients = useMemo(() => {
    if (prospectAppointments.length === 0) return patients;
    const prospects = prospectAppointments.map(app => ({
      id: app.patientId,
      name: app.prospectName && app.prospectName.trim().length > 0 ? app.prospectName : 'New patient intake',
      email: '',
      nextContact: null,
      hasCriticalAllergy: false,
      noShowCount: 0
    }));
    return [...patients, ...prospects];
  }, [patients, prospectAppointments]);

  const displayAppointments = useMemo(() => {
    if (prospectAppointments.length === 0) return appointments;
    return [...appointments, ...prospectAppointments];
  }, [appointments, prospectAppointments]);

  const eventLookup = useMemo(() => {
    const map = new Map<number, CalendarEvent>();
    events.forEach(ev => map.set(ev.id, ev));
    return map;
  }, [events]);

  const fetchAppointments = useCallback(async (options?: { force?: boolean }) => {
    if (!options?.force && (showModal || showEventModal)) {
      return;
    }
    const canUseWeekCache = view === 'week' && !selectedDateRange;
    try {
      // Check cache first (only if not forcing)
      if (!options?.force && canUseWeekCache) {
        const cachedAppointments = getCachedWeekAppointments(currentDate);
        if (cachedAppointments) {
          setAppointments(cachedAppointments);
          // If cache is very fresh (< 1 min), don't refetch from API
          // This prevents lag when switching pages and coming back
          if (isCacheVeryFresh()) {
            return;
          }
        }
      }

      // Cache miss or force refresh - fetch from API
      const start = visibleDateRange.start.toISOString();
      const end = visibleDateRange.end.toISOString();
      const url = `${getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENTS)}?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&includeUndated=true`;
      
      const res = await fetch(url, { headers: getAuthHeaders(token) });
      if (!res.ok) throw new Error('Failed to fetch appointments');
      const data = await res.json();
      
      const normalized = Array.isArray(data)
        ? data.map((app: any) => {
            const treatmentLink = app.treatment && typeof app.treatment === 'object'
              ? {
                  id: app.treatment.id,
                  name: app.treatment.name,
                  cost: app.treatment.cost,
                  recommendedPrice: app.treatment.recommendedPrice,
                  additionalUseFee: app.treatment.additionalUseFee,
                  doctorId: app.treatment.doctorId ?? null,
                  payments: Array.isArray(app.treatment.payments) ? app.treatment.payments : [],
                  installments: Array.isArray(app.treatment.installments) ? app.treatment.installments : []
                }
              : null;
            const linkedTreatments = Array.isArray(app.treatments)
              ? app.treatments
                  .filter((treatment: any) => treatment && typeof treatment.id === 'number')
                  .map((treatment: any) => ({
                    id: treatment.id,
                    name: treatment.name,
                    cost: treatment.cost,
                    recommendedPrice: treatment.recommendedPrice,
                    additionalUseFee: treatment.additionalUseFee,
                    doctorId: treatment.doctorId ?? null,
                    payments: Array.isArray(treatment.payments) ? treatment.payments : [],
                    installments: Array.isArray(treatment.installments) ? treatment.installments : []
                  }))
              : [];
            const appointmentPayments = Array.isArray(app.payments)
              ? app.payments.filter((p: any) => p && (p.treatmentId === null || typeof p.treatmentId === 'undefined'))
              : [];
            const amountPaid = appointmentPayments.reduce((sum: number, payment: any) => {
              const value = typeof payment?.amount === 'number' ? payment.amount : 0;
              return Number.isFinite(value) ? sum + value : sum;
            }, 0);
            const prospectName = typeof app.prospectName === 'string' ? app.prospectName.trim() : '';
            const canceledAt = app.canceledAt ?? app.CanceledAt ?? null;
            const canceledByUserId = app.canceledByUserId ?? app.CanceledByUserId ?? null;
            const canceledBy = app.canceledBy ?? app.CanceledBy ?? null;
            const cancellationNote = app.cancellationNote ?? app.CancellationNote ?? null;
            const cancellationLeadTimeMinutes = app.cancellationLeadTimeMinutes ?? app.CancellationLeadTimeMinutes ?? null;
            return {
              ...app,
              notes: typeof app.notes === 'string' ? app.notes : '',
              isProspect: !app.patientId && prospectName.length > 0,
              status: app.status ?? app.Status ?? null,
              canceledAt,
              canceledByUserId,
              canceledBy,
              cancellationNote,
              isLateCancellation: !!(app.isLateCancellation ?? app.IsLateCancellation),
              cancellationLeadTimeMinutes,
              patient: app.patient ? {
                ...app.patient,
                name: repairTextEncoding(app.patient.name ?? ''),
                hc: getEffectiveHc(app.patient.hc ?? app.patient.HC),
                profilePicturePath: app.patient.profilePicturePath ?? app.patient.ProfilePicturePath ?? null
              } : app.patient,
              treatment: treatmentLink,
              treatments: linkedTreatments.length > 0 ? linkedTreatments : (treatmentLink ? [treatmentLink] : []),
              treatmentId: typeof app.treatmentId === 'number' ? app.treatmentId : (treatmentLink?.id ?? null),
              amountPaid
            };
          })
        : [];
      
      // Cache only default week ranges; custom spans use direct range fetches.
      if (canUseWeekCache) {
        cacheWeekAppointments(normalized, currentDate);
      }
      setAppointments(normalized);
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
    }
  }, [currentDate, selectedDateRange, showEventModal, showModal, token, view, visibleDateRange]);
  const fetchPatients = useCallback(async () => {
    try {
      // Check persistent cache first
      const cachedPatients = getCachedPatients<Patient>();
      if (cachedPatients) {
        const hasBalanceField = cachedPatients.some((p: any) => typeof p?.balanceDue === 'number');
        if (!hasBalanceField) {
          clearPatientsCache();
        } else {
        setPatients(cachedPatients.map((p: any) => ({
          ...p,
          name: repairTextEncoding(p.name ?? ''),
          presentedName: repairTextEncoding(p.presentedName ?? p.name ?? ''),
          hc: getEffectiveHc(p.hc || p.HC),
          profilePicturePath: p.profilePicturePath ?? p.ProfilePicturePath ?? null,
          cancellationCount: typeof p.cancellationCount === 'number' ? p.cancellationCount : (typeof p.CancellationCount === 'number' ? p.CancellationCount : 0),
          lateCancellationCount: typeof p.lateCancellationCount === 'number' ? p.lateCancellationCount : (typeof p.LateCancellationCount === 'number' ? p.LateCancellationCount : 0)
        })));
        setPatientsCached(true);
        return;
        }
      }

      // Cache miss - fetch from API
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENTS), { headers: getAuthHeaders(token) });
      if (res.ok) {
        const data = await res.json();
        const normalized = Array.isArray(data)
          ? data.map((p: any) => ({
              ...p,
              name: repairTextEncoding(p.name ?? ''),
              presentedName: repairTextEncoding(p.presentedName ?? p.name ?? ''),
              hc: getEffectiveHc(p.hc || p.HC),
              profilePicturePath: p.profilePicturePath ?? p.ProfilePicturePath ?? null,
              hasCriticalAllergy: !!p.hasCriticalAllergy,
              noShowCount: typeof p.noShowCount === 'number' ? p.noShowCount : 0,
              cancellationCount: typeof p.cancellationCount === 'number' ? p.cancellationCount : (typeof p.CancellationCount === 'number' ? p.CancellationCount : 0),
              lateCancellationCount: typeof p.lateCancellationCount === 'number' ? p.lateCancellationCount : (typeof p.LateCancellationCount === 'number' ? p.LateCancellationCount : 0),
              balanceDue: typeof p.balanceDue === 'number'
                ? p.balanceDue
                : (typeof p.BalanceDue === 'number' ? p.BalanceDue : 0)
            }))
          : [];
        
        // Cache the results
        setCachedPatients(normalized);
        setPatients(normalized);
        setPatientsCached(true);
      }
    } catch (err) {
      console.error('Failed to fetch patients:', err);
    }
  }, [token]);
  const fetchDoctors = useCallback(async () => {
    try {
      // Check persistent cache first
      const cachedDoctors = getCachedDoctors<Doctor>();
      if (cachedDoctors) {
        setDoctors(cachedDoctors);
        setDoctorsCached(true);
        return;
      }

      // Cache miss - fetch from API
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.DOCTORS), { headers: getAuthHeaders(token) });
      if (res.ok) {
        const data = await res.json();
        setCachedDoctors(data);
        setDoctors(data);
        setDoctorsCached(true);
      }
    } catch (err) {
      console.error('Failed to fetch doctors:', err);
    }
  }, [token]);
  const fetchLaboratories = useCallback(async () => {
    try {
      // Check persistent cache first
      const cachedLaboratories = getCachedLaboratories<Laboratory>();
      if (cachedLaboratories) {
        setLaboratories(cachedLaboratories);
        setLaboratoriesCached(true);
        return;
      }

      // Cache miss - fetch from API
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.LABORATORIES), { headers: getAuthHeaders(token) });
      if (res.ok) {
        const data = await res.json();
        setCachedLaboratories(data);
        setLaboratories(data);
        setLaboratoriesCached(true);
      }
    } catch (err) {
      console.error('Failed to fetch laboratories:', err);
    }
  }, [token]);
  const fetchEvents = useCallback(async (options?: { force?: boolean }) => {
    if (!options?.force && eventsCached) return;
    try { const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.EVENTS), { headers: getAuthHeaders(token) }); if (res.ok) { setEvents(await res.json()); setEventsCached(true); } } catch (err) { console.error('Failed to fetch events:', err); }
  }, [token, eventsCached]);
  const upsertEvent = useCallback((event: CalendarEvent) => {
    setEvents(prev => {
      const exists = prev.some(item => item.id === event.id);
      return exists
        ? prev.map(item => item.id === event.id ? { ...item, ...event } : item)
        : [...prev, event];
    });
    setEventsCached(true);
  }, []);
  const syncGoogleCalendarForWeek = useCallback(async (date: Date, calendarId: string): Promise<boolean> => {
    try {
      const response = await downloadGoogleCalendarWeek(token, date, calendarId);

      if (!response.ok) {
        const message = await response.text();
        console.error('Failed to sync Google Calendar:', message);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Failed to sync Google Calendar:', err);
      return false;
    }
  }, [token]);

  const pushAppointmentsToGoogle = useCallback(
    (date: Date, calendarId: string): Promise<GoogleCalendarPushResult> =>
      uploadGoogleCalendarWeek(token, date, calendarId),
    [token]
  );

  const ensureGoogleCalendarConnected = useCallback(async (): Promise<boolean> => {
    try {
      const statusRes = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.CALENDAR_STATUS), {
        headers: getAuthHeaders(token)
      });
      if (statusRes.ok) {
        const status = await statusRes.json();
        if (!status.isConnected) {
          alert('Google Calendar is not connected. Please go to the Google Calendar authorization page to connect it.');
          return false;
        }
      }
    } catch {
      // Let the transfer request report the detailed error.
    }
    return true;
  }, [token]);

  const handleUploadToGoogle = useCallback(async () => {
    if (!await ensureGoogleCalendarConnected()) return;
    if (!selectedCalendarId) {
      alert('Select a Google Calendar before uploading.');
      return;
    }

    setCalendarTransfer('upload');
    try {
      const result = await pushAppointmentsToGoogle(currentDate, selectedCalendarId);
      if ((result.errors ?? []).length > 0) {
        alert(`Upload completed with errors:\n\n${result.errors.slice(0, 5).join('\n')}`);
      } else {
        alert(`${result.pushed} appointment(s) uploaded to Google Calendar.${result.deleted > 0 ? `\n${result.deleted} deleted appointment(s) removed from Google.` : ''}`);
      }
    } catch (err) {
      alert(`Upload failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setCalendarTransfer(null);
    }
  }, [currentDate, ensureGoogleCalendarConnected, pushAppointmentsToGoogle, selectedCalendarId]);

  const handleDownloadFromGoogle = useCallback(async () => {
    if (!await ensureGoogleCalendarConnected()) return;
    if (!selectedCalendarId) {
      alert('Select a Google Calendar before downloading.');
      return;
    }

    setCalendarTransfer('download');
    try {
      const imported = await syncGoogleCalendarForWeek(currentDate, selectedCalendarId);
      if (!imported) {
        throw new Error('Google Calendar import request failed.');
      }
      await Promise.all([
        fetchAppointments({ force: true }),
        fetchEvents({ force: true })
      ]);
      alert('Google Calendar appointments downloaded to Clinix.');
    } catch (err) {
      alert(`Download failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setCalendarTransfer(null);
    }
  }, [currentDate, ensureGoogleCalendarConnected, fetchAppointments, fetchEvents, selectedCalendarId, syncGoogleCalendarForWeek]);

  const handleReconnectGoogle = useCallback(async () => {
    try { await googleStaffAuth('/calendar/authorize', token); window.location.reload(); }
    catch { alert(t('staff.errors.googleAuthorizationFailed')); }
  }, [token]);

  const handleOpenCalendarPicker = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.CALENDAR_LIST), { headers: getAuthHeaders(token) });
      if (res.ok) {
        const list = await res.json();
        setCalendarList(list);
        setShowCalendarPicker(true);
      }
    } catch { }
  }, [token]);

  const handleAllDayClick = useCallback((date: Date) => {
    setEditingAppointment(null);
    setForm({
      patientId: '',
      doctorId: filterDoctorId || '',
      secondDoctorId: '',
      date: toLaPazDateInput(date),
      startTime: '',
      endTime: '',
      duration: '15',
      notes: '',
      color: '#8b5cf6',
      marginColor: '',
      room: '',
      amountPaid: '',
      treatmentId: '',
      treatmentIds: [],
      treatmentTemplateId: DEFAULT_APPOINTMENT_REASON_VALUE,
      treatmentTemplateSelections: [],
      customReason: t('calendar.modal.defaultConsultationReason', 'Consultation'),
      newTreatmentName: '',
      newTreatmentCost: '',
      newPatientName: '',
      newPatientContact: '',
      newPatientNotes: '',
      isNoShow: false,
      status: '',
      isAllDay: true,
      chargeAmount: '',
      treatedTeeth: []
    });
    setShowModal(true);
  }, [filterDoctorId, t]);

  const handleMoveToAllDay = useCallback(async (id: number, day: Date) => {
    const appt = appointments.find(a => a.id === id);
    if (!appt) return;
    const anchor = new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate(), 12));
    const startDate = makeDateInTimeZone(anchor, 0, 0);
    const endDate = new Date(startDate.getTime() + 24 * 60 * 60 * 1000);
    const treatmentId = appt.treatmentId ?? (appt.treatment as any)?.id ?? null;
    const payload = {
      patientId: appt.patientId,
      doctorId: appt.doctorId ?? null,
      secondDoctorId: appt.secondDoctorId ?? null,
      startTime: startDate.toISOString(),
      endTime: endDate.toISOString(),
      duration: appt.duration ?? 15,
      notes: appt.notes ?? '',
      color: appt.color,
      marginColor: appt.marginColor ?? null,
      isNoShow: !!appt.isNoShow,
      treatmentId,
      room: null,
      isAllDay: true,
      prospectName: appt.prospectName ?? null,
      prospectContact: appt.prospectContact ?? null,
      prospectNotes: appt.prospectNotes ?? null
    };
    setAppointments(prev => prev.map(a => a.id === id
      ? { ...a, startTime: startDate.toISOString(), endTime: endDate.toISOString(), isAllDay: true, status: null, room: null }
      : a
    ));
    try {
      const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${id}`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to move appointment to all-day');
      invalidateAppointmentCache();
      fetchAppointments({ force: true });
    } catch (err) {
      console.error(err);
      setAppointments(prev => prev.map(a => a.id === id
        ? { ...a, startTime: appt.startTime, endTime: appt.endTime, status: appt.status, room: appt.room }
        : a
      ));
    }
  }, [appointments, fetchAppointments, token]);

  const handleSelectCalendar = useCallback(async (calId: string, calName: string) => {
    try {
      await fetch(getApiUrl(API_CONFIG.ENDPOINTS.CALENDAR_SELECT), {
        method: 'POST',
        headers: { ...getAuthHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ calendarId: calId })
      });
      setSelectedCalendarId(calId);
      setSelectedCalendarName(calName);
      setShowCalendarPicker(false);
    } catch (err) {
      alert('Failed to select calendar: ' + (err instanceof Error ? err.message : String(err)));
    }
  }, [token]);

  // Tick for current time line
  useEffect(() => { const id = window.setInterval(() => setNowTick(Date.now()), 30000); return () => clearInterval(id); }, []);
  // Fetch connected Google Calendar status on mount
  useEffect(() => {
    if (!canManageGoogle) return;
    fetch(getApiUrl(API_CONFIG.ENDPOINTS.CALENDAR_STATUS), { headers: getAuthHeaders(token) })
      .then(r => r.ok ? r.json() : null)
      .then(s => {
        if (s?.isConnected) {
          setCalendarEmail(s.calendarEmail ?? null);
          setSelectedCalendarId(s.selectedCalendarId ?? null);
          setSelectedCalendarName(s.selectedCalendarName ?? null);
        }
      })
      .catch(() => {});
  }, [token]);
  // Initial load - fetch all data once
  useEffect(() => { fetchAppointments(); fetchPatients(); fetchDoctors(); fetchLaboratories(); fetchEvents(); }, [fetchAppointments, fetchPatients, fetchDoctors, fetchLaboratories, fetchEvents]);

  // Debounced global appointment search across all dates
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2) {
      setGlobalSearchResults(null);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENT_SEARCH)}?q=${encodeURIComponent(trimmed)}`,
          { headers: getAuthHeaders(token) }
        );
        if (res.ok) {
          const data: Appointment[] = await res.json();
          setGlobalSearchResults(data);
        }
      } catch { /* ignore network errors silently */ } finally {
        setSearchLoading(false);
      }
    }, 400);
  }, [searchQuery, token]);
  // Polling - only refresh appointments and events (events can change from external calendars)
  useEffect(() => {
    const POLL_MS = 15000; const tick = () => { if (!showModal) { fetchAppointments(); fetchEvents({ force: true }); } };
    const interval = window.setInterval(tick, POLL_MS);
    const handleVisibility = () => { if (document.visibilityState === 'visible') tick(); };
    const handleFocus = () => tick();
    window.addEventListener('focus', handleFocus); document.addEventListener('visibilitychange', handleVisibility);
    return () => { clearInterval(interval); window.removeEventListener('focus', handleFocus); document.removeEventListener('visibilitychange', handleVisibility); };
  }, [fetchAppointments, fetchEvents, showModal]);

  // Filtered data
  const filteredAppointments = useMemo(() => {
    return displayAppointments.filter(app => {
      if (!showCanceledOrNoShow && isCanceledOrNoShowAppointment(app)) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const patientName = patients.find(p => p.id === app.patientId)?.name.toLowerCase() || '';
        const notes = app.notes?.toLowerCase() || '';
        const appTreatments = (app as Appointment).treatments;
        const treatmentName = [
          ...(Array.isArray(appTreatments) ? appTreatments.map(treatment => treatment?.name) : []),
          (app as Appointment).treatment?.name
        ].filter(Boolean).join(' ').toLowerCase();
        if (!patientName.includes(q) && !notes.includes(q) && !treatmentName.includes(q)) return false;
      }
      return true;
    });
  }, [displayAppointments, searchQuery, patients, showCanceledOrNoShow]);

  const filteredEvents = useMemo(() => {
    return events.filter(ev => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const title = ev.title?.toLowerCase() || '';
        const notes = ev.notes?.toLowerCase() || '';
        const prospectName = ev.prospectName?.toLowerCase() || '';
        if (!title.includes(q) && !notes.includes(q) && !prospectName.includes(q)) return false;
      }
      return true;
    });
  }, [events, searchQuery]);

  const searchResults = useMemo(() => {
    if (!searchQuery) return null;

    const map = new Map<string, (Appointment | CalendarEvent)[]>();

    // When globalSearchResults is available, use those (all-dates backend search).
    // Otherwise fall back to filtering the currently-loaded week data.
    const sourceAppointments = (globalSearchResults ?? filteredAppointments)
      .filter(app => showCanceledOrNoShow || !isCanceledOrNoShowAppointment(app));

    sourceAppointments.forEach(app => {
      if (!app.startTime) return;
      const d = new Date(app.startTime);
      const key = d.toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(app);
    });

    // Include filtered events only when using the current-week fallback
    if (!globalSearchResults) {
      filteredEvents.forEach(ev => {
        if (!ev.startTime) return;
        const d = new Date(ev.startTime);
        const key = d.toDateString();
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(ev);
      });
    }

    const sortedKeys = Array.from(map.keys()).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    
    return sortedKeys.map(key => ({
      date: new Date(key),
      items: map.get(key)!.sort((a, b) => {
        const tA = a.startTime ? new Date(a.startTime).getTime() : 0;
        const tB = b.startTime ? new Date(b.startTime).getTime() : 0;
        return tA - tB;
      })
    }));
  }, [filteredAppointments, filteredEvents, searchQuery, globalSearchResults, showCanceledOrNoShow]);

  // Imported appointments spanning a full day are also rendered in the all-day row.
  // (can happen when imported from Google Calendar or via data issues)
  const isEffectivelyAllDay = (a: Appointment) =>
    a.isAllDay ||
    (!!a.startTime && !!a.endTime &&
      new Date(a.endTime).getTime() - new Date(a.startTime).getTime() >= 24 * 60 * 60 * 1000);

  const getAppointmentsForDay = (date: Date) => filteredAppointments.filter(a => {
    if (!a.startTime || isEffectivelyAllDay(a as Appointment)) return false;
    return new Date(a.startTime).toDateString() === date.toDateString();
  });
  const getAllDayAppointmentsForDay = (date: Date) => filteredAppointments.filter(a => {
    if (!a.startTime || !isEffectivelyAllDay(a as Appointment)) return false;
    return new Date(a.startTime).toDateString() === date.toDateString();
  }) as Appointment[];
  const undatedAppointments = useMemo(() => appointments.filter(a =>
    !a.startTime &&
    !a.isAllDay &&
    (showCanceledOrNoShow || !isCanceledOrNoShowAppointment(a))
  ), [appointments, showCanceledOrNoShow]);

  const getEventsForDay = (date: Date) => {
    const dayEvents: CalendarEvent[] = [];
    filteredEvents.forEach(ev => {
      if (!ev.startTime || !ev.endTime) return;
      const start = new Date(ev.startTime);
      const end = new Date(ev.endTime);
      
      let matches = false;
      if (start.toDateString() === date.toDateString()) {
        matches = true;
      } else if (ev.recurrenceRule && ev.recurrenceEndDate) {
        const recurrenceEnd = new Date(ev.recurrenceEndDate);
        recurrenceEnd.setHours(23, 59, 59, 999);
        if (date <= recurrenceEnd && date >= start) {
          if (ev.recurrenceRule === 'DAILY') matches = true;
          else if (ev.recurrenceRule === 'WEEKLY' && date.getDay() === start.getDay()) matches = true;
          else if (ev.recurrenceRule === 'MONTHLY' && date.getDate() === start.getDate()) matches = true;
        }
      }

      if (matches) {
        const instanceStart = new Date(date);
        instanceStart.setHours(start.getHours(), start.getMinutes(), 0, 0);
        const durationMs = end.getTime() - start.getTime();
        const instanceEnd = new Date(instanceStart.getTime() + durationMs);
        dayEvents.push({ ...ev, startTime: instanceStart.toISOString(), endTime: instanceEnd.toISOString() });
      }
    });
    return dayEvents;
  };
  // Patients with NextContact matching date
  const getNextContactsForDay = (date: Date) => {
    const ymd = date.toISOString().split('T')[0];
    return patients.filter(p => p.nextContact && p.nextContact.startsWith(ymd));
  };

  // Time window
  const HOUR_HEIGHT = 60; const hoursRangeForWeek = { start: 8, end: 22 } as const;
  const timeSlots = useMemo(() => {
    const s: string[] = [];
    for (let h = hoursRangeForWeek.start; h < hoursRangeForWeek.end; h++) s.push(`${h.toString().padStart(2, '0')}:00`);
    return s;
  }, [hoursRangeForWeek.start, hoursRangeForWeek.end]);

  // Notifications 30 min before appointment
  const scheduledNotifs = useRef<Map<string, number>>(new Map());
  const shownNotifs = useRef<Set<string>>(new Set());

  const showNotification = useCallback((app: Appointment) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    if (!app.startTime) return;
    const start = new Date(app.startTime);
    const patient = patients.find(p => p.id === app.patientId);
    const doc = app.doctorId ? doctors.find(d => d.id === app.doctorId) : undefined;
    const title = `In 30 min: ${formatTime(start)}`;
    const parts: string[] = [];
    if (patient?.name) {
      const nameWithHc = patient.hc ? `${patient.name} - ${patient.hc}` : patient.name;
      parts.push(nameWithHc);
    }
    if (doc?.firstName) parts.push(`Dr ${doc.firstName}`);
    const linkedTreatmentNames = Array.isArray(app.treatments)
      ? app.treatments.map(treatment => treatment?.name).filter(Boolean)
      : [];
    if (linkedTreatmentNames.length > 0) parts.push(linkedTreatmentNames.join(', '));
    else if (app.treatment?.name) parts.push(app.treatment.name);
    try { new Notification(title, { body: parts.join(' • ') }); } catch {}
  }, [patients, doctors]);

  useEffect(() => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') { try { Notification.requestPermission().catch(() => {}); } catch {} }
    
    const now = Date.now();
    const keys = new Set<string>();
    
    for (const app of appointments) {
      if (!app.startTime) continue;
      const start = new Date(app.startTime).getTime();
      if (!isFinite(start) || start <= now) continue;
      
      const key = `${app.id}-${app.startTime}`;
      keys.add(key);
      
      if (shownNotifs.current.has(key)) continue;

      if (!scheduledNotifs.current.has(key)) {
        const delay = Math.max(0, start - 30*60*1000 - now);
        const t = window.setTimeout(() => {
          showNotification(app);
          scheduledNotifs.current.delete(key);
          shownNotifs.current.add(key);
        }, delay);
        scheduledNotifs.current.set(key, t);
      }
    }
    
    scheduledNotifs.current.forEach((t, key) => {
      if (!keys.has(key)) {
        clearTimeout(t);
        scheduledNotifs.current.delete(key);
      }
    });
  }, [appointments, showNotification]);
  useEffect(() => () => { scheduledNotifs.current.forEach(t => clearTimeout(t)); scheduledNotifs.current.clear(); }, []);

  // Mini month + navigation
  const weekDays = useMemo(() => {
    if (view === 'day') return [currentDate];
    return getInclusiveDateRange(visibleDateRange.start, visibleDateRange.end);
  }, [currentDate, view, visibleDateRange]);
  const selectedWeekRange = useMemo(() => ({ start: weekDays[0], end: weekDays[weekDays.length - 1] }), [weekDays]);
  const isCompactRangeView = view === 'week' && weekDays.length > 7;
  const monthWeeks = useMemo(() => getMonthGrid(currentDate), [currentDate]);
  const navigateBy = useCallback((direction: 1 | -1) => {
    setSlideDirection(direction > 0 ? 'next' : 'prev');
    setSelectedDateRange(null);
    setCurrentDate(date => {
      const next = new Date(date);
      if (view === 'month') {
        next.setDate(1);
        next.setMonth(next.getMonth() + direction);
      } else {
        next.setDate(next.getDate() + direction * (view === 'threeDay' ? 3 : view === 'week' ? 7 : 1));
      }
      return next;
    });
  }, [view]);
  const goPrevious = useCallback(() => navigateBy(-1), [navigateBy]);
  const goNext = useCallback(() => navigateBy(1), [navigateBy]);
  const loadMoreScheduleDays = useCallback(() => {
    setScheduleSpanDays(span => Math.min(SCHEDULE_MAX_DAYS, span + SCHEDULE_PAGE_DAYS));
  }, []);
  const openPhoneDay = useCallback((day: Date) => {
    setSlideDirection(null);
    setSelectedDateRange(null);
    setCurrentDate(day);
    setView('day');
  }, []);
  useSwipeNavigation(swipeAreaRef, {
    onPrev: goPrevious,
    onNext: goNext,
    enabled: isPhone && view !== 'schedule' && !searchQuery
  });
  const goToday = () => { const now = new Date(); setSlideDirection(null); setSelectedDateRange(null); setCurrentDate(now); const m = new Date(now); m.setDate(1); setMonthAnchor(m); };
  const handleViewChange = useCallback((nextView: CalendarView) => {
    setSelectedDateRange(null);
    setView(nextView);
  }, []);
  const handleMiniDayClick = useCallback((day: Date) => {
    setSelectedDateRange(null);
    setCurrentDate(day);
  }, []);
  const handleMiniRangeSelect = useCallback((start: Date, end: Date) => {
    const range = normalizeDateRange(start, end);
    setSelectedDateRange(range);
    setCurrentDate(range.start);
    setView('week');
  }, []);

  // Auto-scroll to current hour when visible
  useEffect(() => {
    const container = document.getElementById('calendarScroll');
    if (!container) return;
    const now = new Date();
    if (weekDays.some(d => d.toDateString() === now.toDateString())) {
      const hourOffset = (now.getHours() - hoursRangeForWeek.start) * HOUR_HEIGHT;
      (container as HTMLElement).scrollTop = Math.max(hourOffset - HOUR_HEIGHT * 2, 0);
    } else {
      (container as HTMLElement).scrollTop = 0;
    }
    const scWidth = (container as HTMLElement).offsetWidth - (container as HTMLElement).clientWidth;
    ['calendarScrollbarSpacer', 'allDayScrollbarSpacer'].forEach(id => {
      const spacer = document.getElementById(id);
      if (spacer) {
        (spacer as HTMLElement).style.width = scWidth > 0 ? scWidth + 'px' : '0px';
      }
    });
  }, [currentDate, weekDays, hoursRangeForWeek.start, HOUR_HEIGHT]);

  const closeAppointmentModal = useCallback(() => {
    setShowModal(false);
    setEditingAppointment(null);
    setProspectEventToConvert(null);
  }, []);

  const closeEventModal = useCallback(() => {
    setShowEventModal(false);
    setEditingEvent(null);
  }, []);

  useEffect(() => {
    if (!pendingIntakeConversion) return;
    const { intake, patient } = pendingIntakeConversion;
    clearPatientsCache();
    fetchPatients();
    setPatients(prev => {
      if (prev.some(p => p.id === patient.id)) return prev;
      return [...prev, patient];
    });
    const start = new Date(intake.startTime);
    const end = new Date(intake.endTime);
    const notesLines = [
      intake.prospectNotes && intake.prospectNotes.trim().length ? intake.prospectNotes.trim() : null,
      intake.prospectContact && intake.prospectContact.trim().length ? `Contact: ${intake.prospectContact.trim()}` : null,
      intake.notes && intake.notes.trim().length ? intake.notes.trim() : null
    ].filter((line): line is string => !!line);

    setProspectEventToConvert(intake.eventId);
    setEditingAppointment(null);
    setForm({
      patientId: String(patient.id),
      doctorId: typeof intake.doctorId === 'number' ? String(intake.doctorId) : '',
      secondDoctorId: '',
      date: toLaPazDateInput(start),
      startTime: toLaPazTimeInput(start),
      endTime: toLaPazTimeInput(end),
      duration: String((end.getTime() - start.getTime()) / 60000),
      notes: notesLines.join('\n'),
      color: '#667eea',
      marginColor: '',
      amountPaid: '',
      treatmentId: '',
      treatmentIds: [],
      treatmentTemplateId: DEFAULT_APPOINTMENT_REASON_VALUE,
      treatmentTemplateSelections: [],
      customReason: t('calendar.modal.defaultConsultationReason', 'Consultation'),
      newTreatmentName: '',
      newTreatmentCost: '',
      newPatientName: '',
      newPatientContact: '',
      newPatientNotes: '',
      isNoShow: false,
      status: '',
      isAllDay: false,
      chargeAmount: '',
      reservationFeeAmount: '50',
      treatedTeeth: []
    });
    setShowModal(true);
    if (onClearPendingIntake) onClearPendingIntake();
  }, [fetchPatients, onClearPendingIntake, pendingIntakeConversion, t]);

  // Handlers
  const handleTimeSlotClick = (date: Date, time: string | null, room?: number) => {
    const [hours] = time ? time.split(':').map(Number) : [9]; // Default to 9 if null
    setEditingAppointment(null);
    setForm({
      patientId: '',
      doctorId: filterDoctorId || '',
      secondDoctorId: '',
      date: toLaPazDateInput(date),
      startTime: time || '',
      endTime: time ? `${(hours + 1).toString().padStart(2, '0')}:00` : '',
      duration: '60',
      notes: '',
      color: '#667eea',
      marginColor: '',
      room: room ? String(room) : '',
      amountPaid: '',
      treatmentId: '',
      treatmentIds: [],
      treatmentTemplateId: DEFAULT_APPOINTMENT_REASON_VALUE,
      treatmentTemplateSelections: [],
      customReason: t('calendar.modal.defaultConsultationReason', 'Consultation'),
      newTreatmentName: '',
      newTreatmentCost: '',
      newPatientName: '',
      newPatientContact: '',
      newPatientNotes: '',
      isNoShow: false,
      status: '',
      isAllDay: false,
      chargeAmount: '',
      reservationFeeAmount: '',
      treatedTeeth: []
    });
    setShowModal(true);
  };
  const handleNewEvent = () => {
    setEditingEvent(null);
    setEventForm({
      doctorId: filterDoctorId || '',
      date: toLaPazDateInput(currentDate),
      startTime: '09:00',
      endTime: '10:00',
      title: DEFAULT_EVENT_TITLE,
      notes: '',
      recurrenceRule: '',
      recurrenceEndDate: ''
    });
    setShowEventModal(true);
  };
  const handleAppointmentClick = (appointment: Appointment, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingAppointment(appointment);
    
    // Initialize form with appointment data
    const start = appointment.startTime ? new Date(appointment.startTime) : null;
    const end = appointment.endTime ? new Date(appointment.endTime) : null;
    const durationMinutes = start && end ? Math.round((end.getTime() - start.getTime()) / 60000) : 0;
    
    setForm({
      patientId: appointment.patientId ? String(appointment.patientId) : '',
      doctorId: appointment.doctorId ? String(appointment.doctorId) : '',
      secondDoctorId: appointment.secondDoctorId ? String(appointment.secondDoctorId) : '',
      date: start ? toLaPazDateInput(start) : '',
      startTime: appointment.isAllDay ? '' : (start ? toLaPazTimeInput(start) : ''),
      endTime: appointment.isAllDay ? '' : (end ? toLaPazTimeInput(end) : ''),
      duration: String(durationMinutes),
      notes: appointment.notes || '',
      color: appointment.color || '#667eea',
      marginColor: appointment.marginColor || '',
      room: appointment.room ? String(appointment.room) : '',
      amountPaid: '',
      treatmentId: appointment.treatments?.[0]?.id
        ? String(appointment.treatments[0].id)
        : appointment.treatmentId
          ? String(appointment.treatmentId)
          : '',
      treatmentIds: appointment.treatments?.length
        ? appointment.treatments.map(treatment => String(treatment.id))
        : appointment.treatmentId
          ? [String(appointment.treatmentId)]
          : [],
      treatmentTemplateId: appointment.treatmentTemplateId ? String(appointment.treatmentTemplateId) : (appointment.customReason ? '__other__' : ''),
      treatmentTemplateSelections: [],
      customReason: appointment.customReason || '',
      newTreatmentName: '',
      newTreatmentCost: '',
      newPatientName: '',
      newPatientContact: '',
      newPatientNotes: '',
      isNoShow: appointment.isNoShow || false,
      status: appointment.status || '',
      isAllDay: !!appointment.isAllDay,
      chargeAmount: appointment.chargeAmount == null ? '' : String(appointment.chargeAmount),
      reservationFeeAmount: appointment.reservationFee?.amount == null
        ? (appointment.reservationFeeAmount == null ? '' : String(appointment.reservationFeeAmount))
        : String(appointment.reservationFee.amount),
      treatedTeeth: parseTreatedTeeth(appointment.treatedTeeth)
    });
    
    setShowModal(true);
  };
  const handleEventClick = (ev: CalendarEvent, e: React.MouseEvent) => { 
    e.stopPropagation(); 
    setEditingEvent(ev); 
    const start = ev.startTime ? new Date(ev.startTime) : null; 
    const end = ev.endTime ? new Date(ev.endTime) : null; 
    setEventForm({ 
      doctorId: ev.doctorId ? String(ev.doctorId) : '', 
      date: start ? toLaPazDateInput(start) : '', 
      startTime: start ? toLaPazTimeInput(start) : '', 
      endTime: end ? toLaPazTimeInput(end) : '', 
      title: ev.title, 
      notes: ev.notes,
      recurrenceRule: ev.recurrenceRule || '',
      recurrenceEndDate: ev.recurrenceEndDate ? toLaPazDateInput(new Date(ev.recurrenceEndDate)) : ''
    }); 
    setShowEventModal(true); 
  };
  const refreshEditedAppointment = useCallback(async () => {
    invalidateAppointmentCache();
    await fetchAppointments({ force: true });
  }, [fetchAppointments]);

  const handleAppointmentStatusChanged = useCallback((appointmentId: number, status: AppointmentStatus) => {
    setAppointments(current => current.map(item => item.id === appointmentId ? { ...item, status } : item));
    setEditingAppointment(current => current?.id === appointmentId ? { ...current, status } : current);
    setForm(current => ({ ...current, status }));
    invalidateAppointmentCache();
  }, []);

  const saveEditingAppointmentDraft = useCallback(async (): Promise<Appointment> => {
    if (!editingAppointment) throw new Error('No appointment selected');
    const selectedTreatmentIds = (form.treatmentIds?.length
      ? form.treatmentIds
      : form.treatmentId
        ? [form.treatmentId]
        : [])
      .map(value => Number(value))
      .filter(value => Number.isFinite(value) && value > 0);
    const legacyTemplate = treatmentTemplates.find(template =>
      String(template.id) === form.treatmentTemplateId ||
      (form.treatmentTemplateId === DEFAULT_APPOINTMENT_REASON_VALUE &&
        (isDefaultAppointmentReasonName(template.name) || isDefaultAppointmentReasonName(template.nameEs))));
    const templateSelectionDrafts = form.treatmentTemplateSelections?.length
      ? form.treatmentTemplateSelections
      : legacyTemplate
        ? [{
            treatmentTemplateId: String(legacyTemplate.id),
            price: String(Number(legacyTemplate.suggestedPrice ?? legacyTemplate.suggestedCost ?? 0))
          }]
        : [];
    const templateSelections = templateSelectionDrafts
      .map(selection => ({
        treatmentTemplateId: Number(selection.treatmentTemplateId),
        price: Number(selection.price)
      }))
      .filter(selection => Number.isFinite(selection.treatmentTemplateId) && selection.treatmentTemplateId > 0 && Number.isFinite(selection.price) && selection.price >= 0);
    const hasTreatmentSelections = selectedTreatmentIds.length > 0 || templateSelections.length > 0;
    const isOtherReason = form.treatmentTemplateId === '__other__';
    const isConsultationReason = form.treatmentTemplateId === DEFAULT_APPOINTMENT_REASON_VALUE;
    if (isOtherReason && !form.customReason.trim()) {
      throw new Error(t('calendar.errors.customReasonRequired', 'Enter a reason for this appointment.'));
    }
    const customReason = isConsultationReason
      ? t('calendar.modal.defaultConsultationReason', 'Consultation')
      : form.customReason.trim();
    const startTime = editingAppointment.isAllDay ? null : parseLaPazDateTime(form.date, form.startTime);
    const endTime = editingAppointment.isAllDay ? null : parseLaPazDateTime(form.date, form.endTime);
    const payload: Record<string, unknown> = {
      patientId: form.patientId ? Number(form.patientId) : editingAppointment.patientId,
      prospectName: editingAppointment.prospectName ?? null,
      prospectContact: editingAppointment.prospectContact ?? null,
      prospectNotes: editingAppointment.prospectNotes ?? null,
      doctorId: form.doctorId ? Number(form.doctorId) : null,
      secondDoctorId: form.secondDoctorId ? Number(form.secondDoctorId) : null,
      startTime: startTime?.toISOString() ?? editingAppointment.startTime,
      endTime: endTime?.toISOString() ?? editingAppointment.endTime,
      duration: Number(form.duration) || editingAppointment.duration || 15,
      notes: form.notes,
      color: form.color || null,
      marginColor: form.marginColor || null,
      room: form.room ? Number(form.room) : null,
      isNoShow: form.isNoShow,
      isAllDay: !!editingAppointment.isAllDay,
      treatmentId: selectedTreatmentIds[0] ?? null,
      treatmentIds: selectedTreatmentIds,
      treatmentTemplateSelections: templateSelections,
      treatmentTemplateId: null,
      customReason: !hasTreatmentSelections && (isOtherReason || isConsultationReason)
        ? customReason
        : null,
      treatedTeeth: (form.treatedTeeth || []).join(',')
    };

    const updateResponse = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${editingAppointment.id}`), {
      method: 'PUT',
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload)
    });
    if (!updateResponse.ok) {
      throw new Error(await readApiError(updateResponse, 'Failed to save appointment details'));
    }

    const latestResponse = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${editingAppointment.id}`), {
      headers: getAuthHeaders(token)
    });
    if (!latestResponse.ok) {
      throw new Error(await readApiError(latestResponse, 'Failed to reload appointment details'));
    }
    const latest: Appointment = await latestResponse.json();
    const linkedIds = (latest.treatments?.length
      ? latest.treatments
      : latest.treatment
        ? [latest.treatment]
        : []).map(treatment => String(treatment.id));
    setEditingAppointment(latest);
    setAppointments(current => current.map(item => item.id === latest.id ? { ...item, ...latest } : item));
    setForm(current => ({
      ...current,
      notes: latest.notes || '',
      treatmentId: linkedIds[0] || '',
      treatmentIds: linkedIds,
      treatmentTemplateId: linkedIds.length > 0
        ? ''
        : latest.treatmentTemplateId
          ? String(latest.treatmentTemplateId)
          : latest.customReason
            ? '__other__'
            : '',
      treatmentTemplateSelections: [],
      customReason: latest.customReason || '',
      treatedTeeth: parseTreatedTeeth(latest.treatedTeeth)
    }));
    invalidateAppointmentCache();
    return latest;
  }, [editingAppointment, form, t, token, treatmentTemplates]);

  const handleReservationFeePayment = useCallback(async (data: { amount: number; date: string; method: string; notes?: string }) => {
    if (!editingAppointment) return;
    const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${editingAppointment.id}/reservation-fee-payments`), {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to record reservation fee payment'));
    const updated = await res.json();
    const mergeUpdate = (appointment: Appointment): Appointment => ({
      ...appointment,
      reservationFee: updated.reservationFee ?? appointment.reservationFee
    });
    setEditingAppointment(current => current ? mergeUpdate(current) : current);
    setAppointments(current => current.map(item => item.id === editingAppointment.id ? mergeUpdate(item) : item));
    void refreshEditedAppointment();
  }, [editingAppointment, refreshEditedAppointment, token]);

  const handleReservationFeeUpdate = useCallback(async (amount: number) => {
    if (!editingAppointment) return;
    const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${editingAppointment.id}/reservation-fee`), {
      method: 'PUT',
      headers: getAuthHeaders(token),
      body: JSON.stringify({ amount })
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to update reservation fee'));
    const reservationFee = await res.json();
    const mergeUpdate = (appointment: Appointment): Appointment => ({ ...appointment, reservationFee });
    setEditingAppointment(current => current ? mergeUpdate(current) : current);
    setAppointments(current => current.map(item => item.id === editingAppointment.id ? mergeUpdate(item) : item));
    void refreshEditedAppointment();
  }, [editingAppointment, refreshEditedAppointment, token]);

  const handleReservationFeeRemove = useCallback(async () => {
    if (!editingAppointment) return;
    const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${editingAppointment.id}/reservation-fee`), {
      method: 'DELETE',
      headers: getAuthHeaders(token)
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to remove reservation fee'));
    const mergeUpdate = (appointment: Appointment): Appointment => ({ ...appointment, reservationFee: null, reservationFeeAmount: null });
    setEditingAppointment(current => current ? mergeUpdate(current) : current);
    setAppointments(current => current.map(item => item.id === editingAppointment.id ? mergeUpdate(item) : item));
    void refreshEditedAppointment();
  }, [editingAppointment, refreshEditedAppointment, token]);

  const handleReservationFeeRefund = useCallback(async (data: { amount: number; date: string; method: string; notes?: string }) => {
    if (!editingAppointment) return;
    const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${editingAppointment.id}/reservation-fee-refunds`), {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to refund reservation fee'));
    const updated = await res.json();
    const mergeUpdate = (appointment: Appointment): Appointment => ({ ...appointment, reservationFee: updated.reservationFee ?? appointment.reservationFee });
    setEditingAppointment(current => current ? mergeUpdate(current) : current);
    setAppointments(current => current.map(item => item.id === editingAppointment.id ? mergeUpdate(item) : item));
    void refreshEditedAppointment();
  }, [editingAppointment, refreshEditedAppointment, token]);

  const handleConvertIntake = useCallback((ev: CalendarEvent) => {
    if (!ev.isNewPatientIntake || !onConvertIntake) return;
    const context: IntakeConversionContext = {
      eventId: ev.id,
      startTime: ev.startTime || '',
      endTime: ev.endTime || '',
      doctorId: typeof ev.doctorId === 'number' ? ev.doctorId : null,
      title: ev.title,
      notes: ev.notes,
      prospectName: ev.prospectName ?? null,
      prospectContact: ev.prospectContact ?? null,
      prospectNotes: ev.prospectNotes ?? null
    };
    onConvertIntake(context);
    closeEventModal();
  }, [closeEventModal, onConvertIntake]);

  // Drag & drop move handlers
  const onMoveEvent = useCallback(async (id: number, newStart: Date, newEnd: Date, recordHistory = true) => {
    let previousSnapshot: { startTime: string | null; endTime: string | null } | null = null;
    try {
      const ev = events.find(item => item.id === id);
      if (!ev) {
        console.error(`Event not found: ${id}`);
        return;
      }

      if (recordHistory && ev.startTime && ev.endTime) {
        setHistory(prev => [...prev, {
          type: 'MOVE_EVENT',
          id: ev.id,
          previousStart: ev.startTime!,
          previousEnd: ev.endTime!
        }]);
      }

      previousSnapshot = {
        startTime: ev.startTime,
        endTime: ev.endTime
      };

      setEvents(prev => prev.map(item =>
        item.id === id
          ? {
              ...item,
              startTime: newStart.toISOString(),
              endTime: newEnd.toISOString()
            }
          : item
      ));

      const payload = {
        doctorId: ev.doctorId ?? null,
        startTime: newStart.toISOString(),
        endTime: newEnd.toISOString(),
        title: ev.title,
        notes: ev.notes,
        recurrenceRule: ev.recurrenceRule ?? null,
        recurrenceEndDate: ev.recurrenceEndDate ?? null,
        isNewPatientIntake: !!ev.isNewPatientIntake,
        prospectName: ev.prospectName ?? null,
        prospectContact: ev.prospectContact ?? null,
        prospectNotes: ev.prospectNotes ?? null
      };

      const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.EVENTS}/${id}`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Failed to move event: ${errText}`);
      }

      fetchEvents();
    } catch (err) {
      console.error('Move event failed', err);
      if (!previousSnapshot) return;
      const snapshot = previousSnapshot;
      setEvents(prev => prev.map(item =>
        item.id === id
          ? {
              ...item,
              startTime: snapshot.startTime,
              endTime: snapshot.endTime
            }
          : item
      ));
    }
  }, [events, fetchEvents, token]);

  const onMoveAppointment = useCallback(async (id: number, newStart: Date | null, newEnd: Date | null, newRoom?: number | null, recordHistory = true) => {
    let previousSnapshot: { startTime: string | null; endTime: string | null; duration?: number; room: number | null } | null = null;
    try {
      const appt = appointments.find(a => a.id === id);
      
      // Handle prospect appointments (negative IDs with prospectEventId)
      if (id < 0 && !appt) {
        const prospectAppt = displayAppointments.find(a => a.id === id);
        if (prospectAppt && prospectAppt.prospectEventId) {
          // This is a prospect appointment, move the underlying event instead
          if (newStart && newEnd) {
            onMoveEvent(prospectAppt.prospectEventId, newStart, newEnd);
          } else {
            // Moving to stand-by (null times) not supported for events
            console.warn('Cannot move event to stand-by area');
          }
          return;
        }
        console.error(`Appointment not found: ${id}`);
        return;
      }
      
      if (!appt) {
        console.error(`Appointment not found: ${id}`);
        return;
      }

      if (recordHistory) {
        setHistory(prev => [...prev, {
          type: 'MOVE_APPOINTMENT',
          id: appt.id,
          previousStart: appt.startTime,
          previousEnd: appt.endTime,
          previousRoom: appt.room ?? null
        }]);
      }

      const treatmentId = appt.treatmentId ?? appt.treatment?.id ?? null;
      // Allow moving appointments without a treatment (e.g., new patient intakes)
      
      let duration = appt.duration || 15;
      
      // If the appointment is currently scheduled (has start/end times), 
      // calculate the duration from the time slot to ensure we preserve the visual duration.
      if (appt.startTime && appt.endTime) {
        const s = new Date(appt.startTime);
        const e = new Date(appt.endTime);
        const currentDuration = Math.round((e.getTime() - s.getTime()) / 60000);
        if (currentDuration > 0) {
          duration = currentDuration;
        }
      }

      if (newStart && newEnd) {
        duration = Math.round((newEnd.getTime() - newStart.getTime()) / 60000);
      }

      // Conflict detection for drag moves (skip for all-day and stand-by moves)
      if (newStart && newEnd) {
        const dragS = Math.floor(newStart.getTime() / 60000);
        const dragE = Math.floor(newEnd.getTime() / 60000);
        const dragDocId = appt.doctorId?.toString();
        const dragRoom = newRoom !== undefined ? newRoom : appt.room;
        const dragConflict = appointments.find(a => {
          if (a.id === id) return false;
          if (a.isAllDay) return false;
          if (!a.startTime || !a.endTime) return false;
          // Skip 24h-span appointments without all-day status
          if (new Date(a.endTime).getTime() - new Date(a.startTime).getTime() >= 24 * 60 * 60 * 1000) return false;
          const aS = Math.floor(new Date(a.startTime).getTime() / 60000);
          const aE = Math.floor(new Date(a.endTime).getTime() / 60000);
          if (dragS < aE && dragE > aS) {
            if (dragDocId && a.doctorId?.toString() === dragDocId) return true;
            if (dragRoom && a.room === dragRoom) return true;
          }
          return false;
        });
        if (dragConflict) {
          const msg = t('calendar.conflicts.title') + '\n' +
            (dragConflict.doctorId?.toString() === appt.doctorId?.toString() ? '\n' + t('calendar.conflicts.doctorBooked') : '') +
            (dragConflict.room === (newRoom !== undefined ? newRoom : appt.room) ? '\n' + t('calendar.conflicts.roomOccupied') : '') +
            '\n\n' + t('calendar.conflicts.proceed');
          if (!window.confirm(msg)) return;
        }
      }

      const payload = {
        patientId: appt.patientId,
        doctorId: appt.doctorId ?? null,
        secondDoctorId: appt.secondDoctorId ?? null,
        startTime: newStart ? newStart.toISOString() : null,
        endTime: newEnd ? newEnd.toISOString() : null,
        duration,
        notes: appt.notes ?? '',
        color: appt.color,
        marginColor: appt.marginColor ?? null,
        isNoShow: !!appt.isNoShow,
        treatmentId,
        // When moving to stand-by (no times), clear the room. Otherwise use provided room or keep existing.
        room: !newStart && !newEnd ? null : (newRoom !== undefined ? newRoom : appt.room),
        isAllDay: !!appt.isAllDay && !(newStart && newEnd),
        prospectName: appt.prospectName ?? null,
        prospectContact: appt.prospectContact ?? null,
        prospectNotes: appt.prospectNotes ?? null
      };

      previousSnapshot = {
        startTime: appt.startTime,
        endTime: appt.endTime,
        duration: appt.duration,
        room: appt.room ?? null
      };

      setAppointments(prev => prev.map(item =>
        item.id === id
          ? {
              ...item,
              startTime: newStart ? newStart.toISOString() : null,
              endTime: newEnd ? newEnd.toISOString() : null,
              duration,
              room: !newStart && !newEnd ? null : (newRoom !== undefined ? newRoom : item.room),
              isAllDay: !!item.isAllDay && !(newStart && newEnd)
            }
          : item
      ));

      const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${id}`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errText = await res.text();
        console.error('Move appointment response:', res.status, errText);
        throw new Error(`Failed to move appointment: ${errText}`);
      }
      invalidateAppointmentCache();
      fetchAppointments({ force: true });
    } catch (err) {
      console.error('Move appointment failed', err);
      if (!previousSnapshot) return;
      const snapshot = previousSnapshot;
      setAppointments(prev => prev.map(item =>
        item.id === id
          ? {
              ...item,
              startTime: snapshot.startTime,
              endTime: snapshot.endTime,
              duration: snapshot.duration,
              room: snapshot.room
            }
          : item
      ));
    }
  // Depends on latest appointment collections and move handlers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments, displayAppointments, fetchAppointments, onMoveEvent, token]);


  const undo = useCallback(async () => {
    if (history.length === 0) return;

    const lastAction = history[history.length - 1];
    setHistory(prev => prev.slice(0, -1));

    try {
      if (lastAction.type === 'MOVE_APPOINTMENT') {
        await onMoveAppointment(
          lastAction.id,
          lastAction.previousStart ? new Date(lastAction.previousStart) : null,
          lastAction.previousEnd ? new Date(lastAction.previousEnd) : null,
          lastAction.previousRoom,
          false
        );
      } else if (lastAction.type === 'MOVE_EVENT') {
        await onMoveEvent(
          lastAction.id,
          new Date(lastAction.previousStart),
          new Date(lastAction.previousEnd),
          false
        );
      } else if (lastAction.type === 'RESIZE_APPOINTMENT') {
        await onMoveAppointment(
          lastAction.id,
          new Date(lastAction.previousStart),
          new Date(lastAction.previousEnd),
          undefined,
          false
        );
      } else if (lastAction.type === 'RESIZE_EVENT') {
        await onMoveEvent(
          lastAction.id,
          new Date(lastAction.previousStart),
          new Date(lastAction.previousEnd),
          false
        );
      } else if (lastAction.type === 'EDIT_APPOINTMENT') {
        const prev = lastAction.previousData;
        const payload = {
          patientId: prev.patientId,
          doctorId: prev.doctorId,
          startTime: prev.startTime,
          endTime: prev.endTime,
          notes: prev.notes,
          color: prev.color,
          marginColor: prev.marginColor,
          isNoShow: prev.isNoShow,
          treatmentId: prev.treatmentId,
        };
        const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${lastAction.id}`), {
          method: 'PUT',
          headers: getAuthHeaders(token),
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Failed to undo edit appointment');
        invalidateAppointmentCache();
        fetchAppointments({ force: true });
      } else if (lastAction.type === 'CREATE_APPOINTMENT') {
        const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${lastAction.id}`), {
          method: 'DELETE',
          headers: getAuthHeaders(token)
        });
        if (!res.ok) throw new Error('Failed to undo create appointment');
        invalidateAppointmentCache();
        fetchAppointments({ force: true });
      } else if (lastAction.type === 'DELETE_APPOINTMENT') {
        const prev = lastAction.data;
        const payload = {
          patientId: prev.patientId,
          doctorId: prev.doctorId,
          startTime: prev.startTime,
          endTime: prev.endTime,
          notes: prev.notes,
          color: prev.color,
          marginColor: prev.marginColor,
          isNoShow: prev.isNoShow,
          treatmentId: prev.treatmentId,
        };
        const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENTS), {
          method: 'POST',
          headers: getAuthHeaders(token),
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Failed to undo delete appointment');
        invalidateAppointmentCache();
        fetchAppointments({ force: true });
      } else if (lastAction.type === 'EDIT_EVENT') {
        const prev = lastAction.previousData;
        const payload = {
          doctorId: prev.doctorId,
          startTime: prev.startTime,
          endTime: prev.endTime,
          title: prev.title,
          notes: prev.notes,
          recurrenceRule: prev.recurrenceRule,
          recurrenceEndDate: prev.recurrenceEndDate,
          isNewPatientIntake: prev.isNewPatientIntake,
          prospectName: prev.prospectName,
          prospectContact: prev.prospectContact,
          prospectNotes: prev.prospectNotes
        };
        const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.EVENTS}/${lastAction.id}`), {
          method: 'PUT',
          headers: getAuthHeaders(token),
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Failed to undo edit event');
        fetchEvents();
      } else if (lastAction.type === 'CREATE_EVENT') {
        const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.EVENTS}/${lastAction.id}`), {
          method: 'DELETE',
          headers: getAuthHeaders(token)
        });
        if (!res.ok) throw new Error('Failed to undo create event');
        fetchEvents();
      } else if (lastAction.type === 'DELETE_EVENT') {
        const prev = lastAction.data;
        const payload = {
          doctorId: prev.doctorId,
          startTime: prev.startTime,
          endTime: prev.endTime,
          title: prev.title,
          notes: prev.notes,
          recurrenceRule: prev.recurrenceRule,
          recurrenceEndDate: prev.recurrenceEndDate,
          isNewPatientIntake: prev.isNewPatientIntake,
          prospectName: prev.prospectName,
          prospectContact: prev.prospectContact,
          prospectNotes: prev.prospectNotes
        };
        const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.EVENTS), {
          method: 'POST',
          headers: getAuthHeaders(token),
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Failed to undo delete event');
        fetchEvents();
      }
    } catch (err) {
      console.error('Undo failed', err);
    }
  // Undo depends on latest history and mutation handlers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, onMoveAppointment, onMoveEvent, token, fetchAppointments, fetchEvents]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo]);

  const handleResizeStart = useCallback((appointment: Appointment, edge: 'top' | 'bottom', columnIndex: number) => {
    if (!appointment.startTime || !appointment.endTime) return;
    const state: ResizeState = {
      item: appointment,
      type: 'appointment',
      edge,
      initialStart: new Date(appointment.startTime),
      initialEnd: new Date(appointment.endTime),
      columnIndex,
      lastDelta: 0
    };
    resizingRef.current = state;
    setResizing(state);
  }, []);

  const handleResize = useCallback((appointment: Appointment, edge: 'top' | 'bottom', deltaMinutes: number, columnIndex: number) => {
    if (!appointment.startTime || !appointment.endTime) return;
    setResizing((prev) => {
      if (prev && prev.item.id === appointment.id && prev.type === 'appointment' && prev.edge === edge && prev.lastDelta === deltaMinutes && prev.columnIndex === columnIndex) {
        return prev;
      }
      const base: ResizeState = prev && prev.item.id === appointment.id && prev.type === 'appointment'
        ? { ...prev, lastDelta: deltaMinutes, columnIndex }
        : {
            item: appointment,
            type: 'appointment',
            edge,
            initialStart: new Date(appointment.startTime!),
            initialEnd: new Date(appointment.endTime!),
            columnIndex,
            lastDelta: deltaMinutes
          };
      resizingRef.current = base;
      return base;
    });
  }, []);

  const handleResizeEnd = useCallback(async (appointment: Appointment, edge: 'top' | 'bottom', deltaMinutes: number, columnIndex: number) => {
    if (!appointment.startTime || !appointment.endTime) return;
    const state = resizingRef.current ?? {
      item: appointment,
      type: 'appointment',
      edge,
      initialStart: new Date(appointment.startTime),
      initialEnd: new Date(appointment.endTime),
      columnIndex,
      lastDelta: deltaMinutes
    };
    const finalDelta = deltaMinutes ?? state.lastDelta;
    resizingRef.current = null;
    setResizing(null);
    if (!finalDelta) return;

    const start = new Date(state.initialStart);
    const end = new Date(state.initialEnd);
    if (edge === 'top') {
      start.setMinutes(start.getMinutes() + finalDelta);
      if (start >= end) start.setTime(end.getTime() - 15 * 60000);
    } else {
      end.setMinutes(end.getMinutes() + finalDelta);
      if (end <= start) end.setTime(start.getTime() + 15 * 60000);
    }

    const dayStart = new Date(state.initialStart);
    dayStart.setHours(hoursRangeForWeek.start, 0, 0, 0);
    if (start < dayStart) start.setTime(dayStart.getTime());
    const dayEnd = new Date(state.initialEnd);
    dayEnd.setHours(hoursRangeForWeek.end, 0, 0, 0);
    if (end > dayEnd) end.setTime(dayEnd.getTime());

    if (end.getTime() - start.getTime() < 15 * 60000) {
      if (edge === 'top') {
        start.setTime(end.getTime() - 15 * 60000);
      } else {
        end.setTime(start.getTime() + 15 * 60000);
      }
    }

    if (start.getTime() === state.initialStart.getTime() && end.getTime() === state.initialEnd.getTime()) return;

    setHistory(prev => [...prev, {
      type: 'RESIZE_APPOINTMENT',
      id: appointment.id,
      previousStart: appointment.startTime!,
      previousEnd: appointment.endTime!
    }]);

    await onMoveAppointment(appointment.id, start, end, appointment.room ?? null, false);
  }, [hoursRangeForWeek.end, hoursRangeForWeek.start, onMoveAppointment]);

  const handleEventResizeStart = useCallback((event: CalendarEvent, edge: 'top' | 'bottom', columnIndex: number) => {
    if (!event.startTime || !event.endTime) return;
    const state: ResizeState = {
      item: event,
      type: 'event',
      edge,
      initialStart: new Date(event.startTime),
      initialEnd: new Date(event.endTime),
      columnIndex,
      lastDelta: 0
    };
    resizingRef.current = state;
    setResizing(state);
  }, []);

  const handleEventResize = useCallback((event: CalendarEvent, edge: 'top' | 'bottom', deltaMinutes: number, columnIndex: number) => {
    if (!event.startTime || !event.endTime) return;
    setResizing((prev) => {
      if (prev && prev.item.id === event.id && prev.type === 'event' && prev.edge === edge && prev.lastDelta === deltaMinutes && prev.columnIndex === columnIndex) {
        return prev;
      }
      const base: ResizeState = prev && prev.item.id === event.id && prev.type === 'event'
        ? { ...prev, lastDelta: deltaMinutes, columnIndex }
        : {
            item: event,
            type: 'event',
            edge,
            initialStart: new Date(event.startTime!),
            initialEnd: new Date(event.endTime!),
            columnIndex,
            lastDelta: deltaMinutes
          };
      resizingRef.current = base;
      return base;
    });
  }, []);

  const handleEventResizeEnd = useCallback(async (event: CalendarEvent, edge: 'top' | 'bottom', deltaMinutes: number, columnIndex: number) => {
    if (!event.startTime || !event.endTime) return;
    const state = resizingRef.current ?? {
      item: event,
      type: 'event',
      edge,
      initialStart: new Date(event.startTime),
      initialEnd: new Date(event.endTime),
      columnIndex,
      lastDelta: deltaMinutes
    };
    const finalDelta = deltaMinutes ?? state.lastDelta;
    resizingRef.current = null;
    setResizing(null);
    if (!finalDelta) return;

    const start = new Date(state.initialStart);
    const end = new Date(state.initialEnd);
    if (edge === 'top') {
      start.setMinutes(start.getMinutes() + finalDelta);
      if (start >= end) start.setTime(end.getTime() - 15 * 60000);
    } else {
      end.setMinutes(end.getMinutes() + finalDelta);
      if (end <= start) end.setTime(start.getTime() + 15 * 60000);
    }

    const dayStart = new Date(state.initialStart);
    dayStart.setHours(hoursRangeForWeek.start, 0, 0, 0);
    if (start < dayStart) start.setTime(dayStart.getTime());
    const dayEnd = new Date(state.initialEnd);
    dayEnd.setHours(hoursRangeForWeek.end, 0, 0, 0);
    if (end > dayEnd) end.setTime(dayEnd.getTime());

    if (end.getTime() - start.getTime() < 15 * 60000) {
      if (edge === 'top') {
        start.setTime(end.getTime() - 15 * 60000);
      } else {
        end.setTime(start.getTime() + 15 * 60000);
      }
    }

    if (start.getTime() === state.initialStart.getTime() && end.getTime() === state.initialEnd.getTime()) return;

    setHistory(prev => [...prev, {
      type: 'RESIZE_EVENT',
      id: event.id,
      previousStart: event.startTime!,
      previousEnd: event.endTime!
    }]);

    try {
      const payload: any = {
        doctorId: event.doctorId ?? null,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        title: event.title,
        notes: event.notes
      };
      if (event.isNewPatientIntake) {
        payload.isNewPatientIntake = true;
        payload.prospectName = event.prospectName ?? null;
        payload.prospectContact = event.prospectContact ?? null;
        payload.prospectNotes = event.prospectNotes ?? null;
      }
      const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.EVENTS}/${event.id}`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to resize event');
      fetchEvents();
    } catch (err) {
      console.error('Resize event failed', err);
    }
  }, [fetchEvents, hoursRangeForWeek.end, hoursRangeForWeek.start, token]);

  // Appointment CRUD
  const handleSubmitAppointment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let startDateTime: Date | null = null;
      let endDateTime: Date | null = null;
      if (form.date && form.startTime && form.endTime) {
        startDateTime = parseLaPazDateTime(form.date, form.startTime);
        endDateTime = parseLaPazDateTime(form.date, form.endTime);
      }
      // Imported all-day appointments retain their date; Clinix cannot create new all-day appointments.
      if (editingAppointment?.isAllDay && form.date && !startDateTime) {
        const parts = form.date.split('-').map(Number);
        startDateTime = makeDateInTimeZone(new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], 12)), 0, 0);
        endDateTime = new Date(startDateTime.getTime() + 24 * 60 * 60 * 1000);
      }

      const patId = parseInt(form.patientId, 10);
      const pat = patients.find(p => p.id === patId);
      const docId = parseInt(form.doctorId, 10);
      const doc = doctors.find(d => d.id === docId);
      const finalRoom = getAutoAssignedRoom({
        selectedRoom: form.room || '',
        startDateTime,
        endDateTime,
        appointments,
        doctor: doc,
        patient: pat,
        editingAppointmentId: editingAppointment?.id
      });

      // Conflict detection — skip entirely when editing and the doctor+time haven't changed
      // (prevents false positives from timezone rounding differences between stored and re-parsed times)
      const editingTimeUnchanged = editingAppointment && startDateTime && endDateTime &&
        Math.floor(new Date(editingAppointment.startTime!).getTime() / 60000) === Math.floor(startDateTime.getTime() / 60000) &&
        Math.floor(new Date(editingAppointment.endTime!).getTime() / 60000) === Math.floor(endDateTime.getTime() / 60000) &&
        (editingAppointment.doctorId?.toString() ?? '') === form.doctorId;

      // Conflict detection (Appointments)
      // Exclude all-day appointments (they span midnight-midnight and would conflict with every timed appointment)
      // Truncate to minutes so back-to-back appointments (touching at boundary) don't trigger false conflicts
      const conflictAppt = editingTimeUnchanged ? undefined : appointments.find(a => {
        if (!startDateTime || !endDateTime) return false;
        if (editingAppointment && a.id === editingAppointment.id) return false;
        if (!a.startTime || !a.endTime) return false;
        if (a.isAllDay) return false;
        // Treat 24h-span appointments without all-day status as all-day (e.g. imported events)
        if (new Date(a.endTime).getTime() - new Date(a.startTime).getTime() >= 24 * 60 * 60 * 1000) return false;
        const newS = Math.floor(startDateTime.getTime() / 60000);
        const newE = Math.floor(endDateTime.getTime() / 60000);
        const aS = Math.floor(new Date(a.startTime).getTime() / 60000);
        const aE = Math.floor(new Date(a.endTime).getTime() / 60000);
        if (newS < aE && newE > aS) {
          if (form.doctorId && a.doctorId && a.doctorId.toString() === form.doctorId) return true;
          // Only flag room conflict when the user explicitly chose a room (not auto-assigned)
          if (form.room && a.room && a.room.toString() === form.room) return true;
        }
        return false;
      });

      // Conflict detection (Events)
      const conflictEvent = editingTimeUnchanged ? undefined : events.find(ev => {
        if (!startDateTime || !endDateTime) return false;
        if (!ev.startTime || !ev.endTime) return false;
        const newS = Math.floor(startDateTime.getTime() / 60000);
        const newE = Math.floor(endDateTime.getTime() / 60000);
        const evS = Math.floor(new Date(ev.startTime).getTime() / 60000);
        const evE = Math.floor(new Date(ev.endTime).getTime() / 60000);
        if (newS < evE && newE > evS) {
          if (form.doctorId && ev.doctorId && ev.doctorId.toString() === form.doctorId) return true;
        }
        return false;
      });

      if (conflictAppt || conflictEvent) {
        let msg = 'Warning: Conflict detected.\n';
        if (conflictAppt) {
          if (form.doctorId && conflictAppt.doctorId?.toString() === form.doctorId) msg += '- Doctor is already booked (Appointment).\n';
          if (form.room && conflictAppt.room?.toString() === form.room) msg += '- Room is already occupied.\n';
        }
        if (conflictEvent) {
          msg += '- Doctor has an event/block scheduled.\n';
        }
        msg += '\nProceed anyway?';
        if (!window.confirm(msg)) return;
      }

      const isNewPatientIntake = !editingAppointment && form.patientId === '__new__';
      const parsedTreatmentId = form.treatmentId ? parseInt(form.treatmentId, 10) : NaN;
      const selectedTreatmentId = Number.isFinite(parsedTreatmentId) && parsedTreatmentId > 0 ? parsedTreatmentId : null;
      const parsedTemplateId = form.treatmentTemplateId ? parseInt(form.treatmentTemplateId, 10) : NaN;
      const selectedTemplate = Number.isFinite(parsedTemplateId) && parsedTemplateId > 0
        ? treatmentTemplates.find(template => template.id === parsedTemplateId) ?? null
        : null;
      const isOtherReason = form.treatmentTemplateId === '__other__';
      const isConsultationReason = form.treatmentTemplateId === DEFAULT_APPOINTMENT_REASON_VALUE;
      if (!selectedTreatmentId && isOtherReason && !form.customReason.trim()) {
        throw new Error(t('calendar.errors.customReasonRequired', 'Enter a reason for this appointment.'));
      }
      const customReason = isConsultationReason
        ? t('calendar.modal.defaultConsultationReason', 'Consultation')
        : form.customReason.trim();
      const selectedTreatment = selectedTreatmentId
        ? patientTreatments.find(treatment => treatment.id === selectedTreatmentId) ?? null
        : null;
      const parsedPatientId = form.patientId ? parseInt(form.patientId, 10) : NaN;
      const selectedPatientId = Number.isFinite(parsedPatientId) && parsedPatientId > 0 ? parsedPatientId : null;
      const selectedPatient = selectedPatientId ? patients.find(patient => patient.id === selectedPatientId) : null;
      const reservationPatient = isNewPatientIntake || isNewPatientStatus(selectedPatient?.status);
      const configuredReservationFeeAmount = Math.max(0, Number(form.reservationFeeAmount || 0));
      const reservationPaymentAmount = reservationPatient ? configuredReservationFeeAmount : 0;
      const shouldKeepProspect = !selectedPatientId && !!editingAppointment?.prospectName;
      const basePayload: any = {
        patientId: isNewPatientIntake ? null : selectedPatientId, // will be replaced below if new patient
        prospectName: shouldKeepProspect ? editingAppointment?.prospectName ?? null : null,
        prospectContact: shouldKeepProspect ? editingAppointment?.prospectContact ?? null : null,
        prospectNotes: shouldKeepProspect ? editingAppointment?.prospectNotes ?? null : null,
        doctorId: form.doctorId ? parseInt(form.doctorId, 10) : null,
        secondDoctorId: form.secondDoctorId ? parseInt(form.secondDoctorId, 10) : null,
        startTime: startDateTime ? startDateTime.toISOString() : null,
        endTime: endDateTime ? endDateTime.toISOString() : null,
        duration: form.duration ? parseInt(form.duration, 10) : 15,
        notes: form.notes || '',
        color: form.color,
        marginColor: form.marginColor ? form.marginColor : null,
        isNoShow: form.isNoShow,
        room: finalRoom ? parseInt(finalRoom, 10) : null,
        isAllDay: !!editingAppointment?.isAllDay,
        chargeAmount: undefined,
        reservationFeeAmount: !editingAppointment && (isNewPatientIntake || selectedPatientId)
          ? configuredReservationFeeAmount
          : undefined,
        reservationFeePayment: !editingAppointment && reservationPatient && form.reservationFeePaid && reservationPaymentAmount > 0.005
          ? {
              amount: reservationPaymentAmount,
              date: new Date().toISOString(),
              method: form.reservationFeePaymentMethod || 'Cash'
            }
          : null,
        treatmentTemplateId: selectedTreatmentId ? null : selectedTemplate?.id ?? null,
        customReason: selectedTreatmentId || selectedTemplate ? null : (isOtherReason || isConsultationReason ? customReason : null),
        treatedTeeth: (form.treatedTeeth || []).join(',')
      };
      let createdPatientForIntake: Patient | null = null;
      let createdAppointmentForIntake: Appointment | null = null;

      if (isNewPatientIntake) {
        // Create a partial patient record. HC is assigned atomically at checkout.
        const newPatientName = form.newPatientName?.trim() || '';
        const newPatientPhone = form.newPatientContact?.trim() || '';
        if (!newPatientName) {
          throw new Error(t('calendar.errors.newPatientNameRequired', 'New patient name is required.'));
        }
        const nameParts = newPatientName.split(' ').filter(Boolean);
        const patientPayload = {
          firstName: nameParts[0] || '',
          firstLastName: nameParts.slice(1).join(' ') || nameParts[0] || '',
          presentedName: newPatientName,
          name: newPatientName,
          phoneNumber: newPatientPhone,
          notes: form.newPatientNotes?.trim() || '',
          email: '',
          address: '',
          referralSource: '',
          status: 'New',
          isForeignPatient: false,
          hc: null
        };
        const patientRes = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENTS), {
          method: 'POST',
          headers: getAuthHeaders(token),
          body: JSON.stringify(patientPayload)
        });
        if (!patientRes.ok) {
          throw new Error(await readApiError(patientRes, t('calendar.errors.createPatient', 'Failed to create patient.')));
        }
        const createdPatient = await patientRes.json();
        createdPatientForIntake = createdPatient;
        basePayload.patientId = createdPatient.id;
        clearPatientsCache();
        setPatients(prev => [...prev, createdPatient]);
      }

      let toggledNoShow = false;
      if (editingAppointment) {
        toggledNoShow = editingAppointment.isNoShow !== form.isNoShow;
        
        setHistory(prev => [...prev, { type: 'EDIT_APPOINTMENT', id: editingAppointment.id, previousData: editingAppointment }]);

        if (toggledNoShow) {
          const toggleRes = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${editingAppointment.id}/no-show`), {
            method: 'PUT',
            headers: getAuthHeaders(token),
            body: JSON.stringify({ isNoShow: form.isNoShow })
          });
          if (!toggleRes.ok) throw new Error('Failed to update no-show');
        }
        const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${editingAppointment.id}`), {
          method: 'PUT',
          headers: getAuthHeaders(token),
          body: JSON.stringify({
            ...basePayload,
            treatmentId: selectedTreatmentId
          })
        });
        if (!res.ok) throw new Error('Failed to update appointment');

        // Immediately reflect the edit in the calendar without waiting for re-fetch
        setAppointments(prev => prev.map(a =>
          a.id === editingAppointment.id
            ? {
                ...a,
                startTime: startDateTime ? startDateTime.toISOString() : null,
                endTime: endDateTime ? endDateTime.toISOString() : null,
                doctorId: form.doctorId ? parseInt(form.doctorId, 10) : null,
                secondDoctorId: form.secondDoctorId ? parseInt(form.secondDoctorId, 10) : null,
                notes: form.notes || '',
                room: finalRoom ? parseInt(finalRoom, 10) : null,
                status: a.status,
                isAllDay: !!editingAppointment?.isAllDay,
                isNoShow: form.isNoShow,
                color: form.color || a.color,
                treatmentId: selectedTreatmentId,
                treatments: selectedTreatment ? [{
                  id: selectedTreatment.id,
                  name: selectedTreatment.name,
                  cost: selectedTreatment.cost
                }] : [],
                treatmentTemplateId: selectedTreatmentId ? null : selectedTemplate?.id ?? null,
                treatmentTemplate: selectedTemplate
                  ? {
                      id: selectedTemplate.id,
                      name: selectedTemplate.name,
                      suggestedPrice: selectedTemplate.suggestedPrice,
                      suggestedCost: selectedTemplate.suggestedCost,
                      expectedDurationMinutes: selectedTemplate.expectedDurationMinutes,
                      suggestedAdditionalUseFee: selectedTemplate.suggestedAdditionalUseFee,
                      defaultDoctorId: selectedTemplate.defaultDoctorId ?? null
                    }
                  : null,
                customReason: selectedTreatmentId || selectedTemplate ? null : (isOtherReason || isConsultationReason ? customReason : null),
                treatedTeeth: (form.treatedTeeth || []).join(',') || null,
              }
            : a
        ));

        if (toggledNoShow) {
          fetchPatients();
        }
      } else {
        const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENTS), {
          method: 'POST',
          headers: getAuthHeaders(token),
          body: JSON.stringify({
            ...basePayload,
            treatmentId: selectedTreatmentId,
            newTreatment: null
          })
        });
        if (!res.ok) throw new Error('Failed to create appointment');
        const created = await res.json();
        if (isNewPatientIntake) createdAppointmentForIntake = created;
        setHistory(prev => [...prev, { type: 'CREATE_APPOINTMENT', id: created.id }]);
        if (isNewPatientIntake || form.reservationFeePaid || (selectedPatientId && !getEffectiveHc(selectedPatient?.hc))) {
          clearPatientsCache();
          void fetchPatients();
        }
      }

      const eventToRemove = prospectEventToConvert;
      if (isNewPatientIntake && createdAppointmentForIntake && createdPatientForIntake) {
        const operationalAppointment = {
          ...createdAppointmentForIntake,
          patientId: createdPatientForIntake.id,
          patient: createdAppointmentForIntake.patient || createdPatientForIntake
        } as Appointment;
        setEditingAppointment(operationalAppointment);
        setForm(current => ({
          ...current,
          patientId: String(createdPatientForIntake!.id),
          status: operationalAppointment.status || 'scheduled'
        }));
        setShowModal(true);
        setProspectEventToConvert(null);
      } else {
        closeAppointmentModal();
      }
      invalidateAppointmentCache();
      fetchAppointments({ force: true });
      if (eventToRemove) {
        try {
          const ev = events.find(e => e.id === eventToRemove);
          if (ev) {
             setHistory(prev => [...prev, { type: 'DELETE_EVENT', data: ev }]);
          }
          const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.EVENTS}/${eventToRemove}`), {
            method: 'DELETE',
            headers: getAuthHeaders(token)
          });
          if (!res.ok) {
            console.error('Failed to remove intake event after conversion');
          }
        } catch (err) {
          console.error('Error removing intake event after conversion', err);
        } finally {
          fetchEvents();
        }
      }
    } catch (err) { console.error('Failed to save appointment:', err); }
  };
  const handleDeleteAppointment = async () => { 
    if (!editingAppointment || !window.confirm(t('appointments.deleteConfirm'))) return; 
    
    setHistory(prev => [...prev, { type: 'DELETE_APPOINTMENT', data: editingAppointment }]);

    try { 
      const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${editingAppointment.id}`), { method: 'DELETE', headers: getAuthHeaders(token) }); 
      if (!res.ok) throw new Error('Failed to delete appointment'); 
      setShowModal(false); 
      invalidateAppointmentCache();
      fetchAppointments({ force: true }); 
    } catch (err) { console.error('Failed to delete appointment:', err); } 
  };

  const renderCompactRangeView = () => {
    const columns = Math.ceil(weekDays.length / 2);

    return (
      <div
        id="calendarScroll"
        data-testid="compact-range-calendar"
        style={{
          flex: 1,
          overflow: 'auto',
          background: calendarChrome.grid,
          padding: 8
        }}
      >
        <div className="responsive-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, minmax(180px, 1fr))`,
            gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
            gap: 8,
            minHeight: '100%'
          }}
        >
          {weekDays.map(day => {
            const isToday = day.toDateString() === new Date(nowTick).toDateString();
            const dayAppointments = [
              ...getAllDayAppointmentsForDay(day),
              ...getAppointmentsForDay(day)
            ].sort((a, b) => {
              const aTime = a.startTime ? new Date(a.startTime).getTime() : 0;
              const bTime = b.startTime ? new Date(b.startTime).getTime() : 0;
              return aTime - bTime;
            });

            return (
              <section
                key={day.toISOString()}
                style={{
                  minHeight: 160,
                  border: `1px solid ${isToday ? tokens.app.primary : calendarChrome.divider}`,
                  borderRadius: 6,
                  background: calendarChrome.panel,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDateRange(null);
                    setCurrentDate(day);
                    setView('day');
                  }}
                  style={{
                    border: 'none',
                    borderBottom: `1px solid ${calendarChrome.divider}`,
                    background: isToday ? calendarChrome.todayBg : calendarChrome.header,
                    color: isToday ? calendarChrome.todayText : calendarChrome.text,
                    padding: '6px 8px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 12
                  }}
                >
                  {formatDate(day)}
                </button>
                <div style={{ padding: 6, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {dayAppointments.map(app => {
                    const patient = patients.find(p => p.id === app.patientId);
                    const embeddedPatientName = (app as Appointment).patient?.name?.trim();
                    const patientName = app.prospectName
                      || patient?.presentedName
                      || patient?.name
                      || (embeddedPatientName && embeddedPatientName.length > 0 ? embeddedPatientName : t('calendar.unknownPatient', 'Unknown patient'));
                    const doctor = app.doctorId ? doctors.find(d => d.id === app.doctorId) : undefined;
                    const stripeColor = doctor?.color || app.color || '#667eea';
                    const isAllDay = isEffectivelyAllDay(app);
                    const timeLabel = isAllDay || !app.startTime
                      ? t('calendar.allDay')
                      : formatTime(new Date(app.startTime));

                    return (
                      <button className="responsive-grid"
                        key={app.id}
                        type="button"
                        data-testid={`compact-appointment-card-${app.id}`}
                        onClick={(event) => handleAppointmentClick(app, event)}
                        onContextMenu={(event) => handleAppointmentContextMenu(app, event)}
                        title={patientName}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '46px minmax(0, 1fr)',
                          gap: 6,
                          alignItems: 'center',
                          border: `1px solid ${calendarChrome.divider}`,
                          borderLeft: `4px solid ${stripeColor}`,
                          borderRadius: 5,
                          background: tokens.app.surfaceAlt,
                          color: tokens.app.text,
                          minHeight: 34,
                          padding: '4px 6px',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        <span style={{ fontSize: 10, color: tokens.app.muted, whiteSpace: 'nowrap' }}>{timeLabel}</span>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {patientName}
                          </span>
                          {doctor && (
                            <span style={{ display: 'block', fontSize: 10, color: tokens.app.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {t('calendar.dr')} {doctor.firstName || doctor.lastName || ''}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                  {dayAppointments.length === 0 && (
                    <div style={{ color: calendarChrome.muted, fontSize: 12, padding: '8px 2px' }}>
                      {t('calendar.noAppointments', 'No appointments')}
                    </div>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    );
  };

  const toolbarProps = {
    canManageGoogle,
    view,
    onViewChange: handleViewChange,
    onToday: goToday,
    onNewEvent: handleNewEvent,
    calendarTransfer,
    onUploadToGoogle: handleUploadToGoogle,
    onDownloadFromGoogle: handleDownloadFromGoogle,
    calendarEmail,
    selectedCalendarId,
    selectedCalendarName,
    calendarList,
    showCalendarPicker,
    onOpenCalendarPicker: handleOpenCalendarPicker,
    onCloseCalendarPicker: () => setShowCalendarPicker(false),
    onSelectCalendar: handleSelectCalendar,
    onReconnectGoogle: handleReconnectGoogle,
    doctors,
    filterDoctorId,
    filterDoctorId2,
    filterRoom,
    showCanceledOrNoShow,
    onFilterDoctorIdChange: setFilterDoctorId,
    onFilterDoctorId2Change: setFilterDoctorId2,
    onFilterRoomChange: setFilterRoom,
    onShowCanceledOrNoShowChange: setShowCanceledOrNoShow,
    searchQuery,
    onSearchQueryChange: setSearchQuery
  };

  const gutterWidth = isPhone ? 44 : 72;
  const isPhoneGridView = isPhone && (view === 'day' || view === 'threeDay');
  const phoneTitle = (() => {
    const sameYear = currentDate.getFullYear() === new Date(nowTick).getFullYear();
    const label = currentDate.toLocaleDateString(i18n.language, { month: 'long', year: sameYear ? undefined : 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  })();
  const slideClass = isPhone && slideDirection ? `phone-cal-slide-${slideDirection}` : undefined;
  const getPhonePatientName = (app: Appointment) => {
    if (app.isProspect) return app.prospectName || t('calendar.newPatientIntake');
    const patient = patients.find(p => p.id === app.patientId);
    const embeddedName = app.patient?.name?.trim();
    return patient?.presentedName || patient?.name || embeddedName || app.customReason || t('calendar.unknownPatient', 'Unknown patient');
  };
  const getPhoneReasonLabel = (app: Appointment) => {
    const linked = Array.isArray(app.treatments) ? app.treatments.map(treatment => treatment?.name).filter(Boolean) : [];
    return linked.length > 0 ? linked.join(', ') : app.treatment?.name || app.treatmentTemplate?.name || app.customReason || '';
  };
  const openPhoneAppointment = (app: Appointment, event: React.MouseEvent) => {
    const prospect = app.isProspect && app.prospectEventId ? eventLookup.get(app.prospectEventId) : undefined;
    if (prospect) handleEventClick(prospect, event);
    else handleAppointmentClick(app, event);
  };
  const phoneNewAppointment = () => {
    const nextHour = Math.min(Math.max(new Date().getHours() + 1, hoursRangeForWeek.start), hoursRangeForWeek.end - 1);
    handleTimeSlotClick(currentDate, `${pad2(nextHour)}:00`, filterRoom ? Number(filterRoom) : undefined);
  };

  return (
    <div className={isPhone ? 'calendar-page calendar-page-phone' : 'calendar-page'} style={calendarContainerStyle}>
      {isPhone ? (
        <>
          <PhoneCalendarHeader
            title={phoneTitle}
            todayNumber={new Date(nowTick).getDate()}
            pickerOpen={phonePickerOpen}
            searchOpen={phoneSearchOpen}
            searchQuery={searchQuery}
            onOpenMenu={onOpenMenu}
            onTogglePicker={() => {
              if (!phonePickerOpen) setMonthAnchor(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
              setPhonePickerOpen(open => !open);
            }}
            onOpenSearch={() => { setPhonePickerOpen(false); setPhoneSearchOpen(true); }}
            onCloseSearch={() => { setPhoneSearchOpen(false); setSearchQuery(''); }}
            onSearchQueryChange={setSearchQuery}
            onToday={() => { goToday(); setPhonePickerOpen(false); }}
            onOpenSheet={() => { setPhonePickerOpen(false); setPhoneSheetOpen(true); }}
          />
          {phonePickerOpen && (
            <div className="phone-cal-picker" style={{ background: calendarChrome.panel, borderBottom: `1px solid ${calendarChrome.divider}` }}>
              <MiniMonth
                monthAnchor={monthAnchor}
                setMonthAnchor={setMonthAnchor}
                selectedWeekStart={startOfDay(currentDate)}
                selectedWeekEnd={endOfDay(currentDate)}
                onDayClick={day => { swallowNextClick(); setSlideDirection(null); handleMiniDayClick(day); setPhonePickerOpen(false); }}
                onToday={() => { goToday(); setPhonePickerOpen(false); }}
              />
            </div>
          )}
        </>
      ) : (
        <CalendarToolbar {...toolbarProps} />
      )}
      <div className="calendar-body responsive-row" style={{ display: 'flex', gap: '24px', flex: 1, overflow: 'hidden' }}>
        {!isPhone && <div className="calendar-mini" style={{ display: showMini ? 'block' : 'none' }}>
          <MiniMonth
            monthAnchor={monthAnchor}
            setMonthAnchor={setMonthAnchor}
            selectedWeekStart={selectedWeekRange.start}
            selectedWeekEnd={selectedWeekRange.end}
            onDayClick={handleMiniDayClick}
            onRangeSelect={handleMiniRangeSelect}
            onToday={goToday}
          />
        </div>}
        <div ref={swipeAreaRef} className="calendar-main" style={{ flex: 1, minWidth: 0, height: '100%' }}>
          {searchQuery && (searchLoading || searchResults) ? (
            <CalendarSearchResults
              query={searchQuery}
              loading={searchLoading}
              groups={searchResults}
              patients={patients}
              doctors={doctors}
              onClear={() => { setSearchQuery(''); setPhoneSearchOpen(false); }}
              onSelectDate={date => {
                setSelectedDateRange(null);
                setCurrentDate(date);
                setSearchQuery('');
                setPhoneSearchOpen(false);
              }}
            />
          ) : isPhone && view === 'schedule' ? (
            <PhoneScheduleView
              days={weekDays}
              nowTick={nowTick}
              doctors={doctors}
              getTimedAppointments={getAppointmentsForDay}
              getAllDayAppointments={getAllDayAppointmentsForDay}
              getEvents={getEventsForDay}
              getPatientName={getPhonePatientName}
              getReasonLabel={getPhoneReasonLabel}
              onOpenAppointment={openPhoneAppointment}
              onAppointmentContextMenu={handleAppointmentContextMenu}
              onEventClick={handleEventClick}
              onDayClick={openPhoneDay}
              onLoadMore={scheduleSpanDays < SCHEDULE_MAX_DAYS ? loadMoreScheduleDays : undefined}
            />
          ) : isPhone && view === 'month' ? (
            <div key={`${currentDate.getFullYear()}-${currentDate.getMonth()}`} className={slideClass} style={{ height: '100%' }}>
              <PhoneMonthView
                weeks={monthWeeks}
                monthDate={currentDate}
                nowTick={nowTick}
                doctors={doctors}
                getAppointments={day => [...getAllDayAppointmentsForDay(day), ...getAppointmentsForDay(day).sort((a, b) => new Date(a.startTime!).getTime() - new Date(b.startTime!).getTime())]}
                getPatientName={getPhonePatientName}
                onDayClick={openPhoneDay}
              />
            </div>
          ) : (
          <div className={isPhone ? 'calendar-grid-panel' : undefined} style={{ background: calendarChrome.panel, border: isPhone ? 'none' : `1px solid ${calendarChrome.frame}`, borderRadius: isPhone ? 0 : '8px', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {!isPhone && <div className="responsive-row" style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 6, padding: '4px 8px 0 8px' }}>
              <button data-testid="mini-month-toggle" onClick={() => setShowMini(v => !v)} style={{ ...calendarButtonStyle, padding: '4px 10px', fontSize: 12 }}>{showMini ? t('calendar.hideMini') : t('calendar.showMini')}</button>
              <span style={{ fontWeight: 700, fontSize: 15, color: calendarChrome.text, marginLeft: 8 }}>
                {view === 'day'
                  ? formatDate(currentDate)
                  : (() => {
                      const first = weekDays[0];
                      const last = weekDays[weekDays.length - 1];
                      if (first.getMonth() === last.getMonth()) {
                        return `${formatDate(first)} – ${formatDate(last)}`;
                      }
                      const m1 = formatDate(first);
                      const m2 = formatDate(last);
                      return `${m1} – ${m2}`;
                    })()
                }
              </span>
            </div>}
            {isCompactRangeView ? renderCompactRangeView() : (
            <>
            {isPhoneGridView ? (
              <PhoneDayHeader
                days={weekDays}
                gutterWidth={gutterWidth}
                nowTick={nowTick}
                onDayClick={view === 'threeDay' ? openPhoneDay : undefined}
              />
            ) : (
            <div className="calendar-grid-row" style={{ display: 'flex', background: calendarChrome.header, borderBottom: `1px solid ${calendarChrome.divider}` }}>
              <div style={{ width: 72, height: 48 }} />
              {view === 'day' ? (
                [1, 2, 3, 4, 5].map((room, index) => (
                  <div key={room} style={{ flex: 1, textAlign: 'center', minHeight: 48, display: 'flex', flexDirection: 'column', justifyContent: 'center', borderLeft: index === 0 ? 'none' : `1px solid ${calendarChrome.divider}`, background: calendarChrome.header, color: calendarChrome.text, fontSize: 13, fontWeight: 600, borderTop: `3px solid ${ROOM_COLORS[room]}` }}>
                    {t('calendar.room')} {room}
                  </div>
                ))
              ) : (
                weekDays.map((day, index) => {
                  const isToday = day.toDateString() === new Date().toDateString();
                  const label = day.toLocaleDateString(i18n.language, { weekday: 'short' });
                  const contacts = getNextContactsForDay(day);
                  return (
                    <div 
                      key={index} 
                      onClick={() => {
                        setSelectedDateRange(null);
                        setCurrentDate(day);
                        setView('day');
                      }}
                      style={{ 
                        flex: 1, 
                        textAlign: 'center', 
                        minHeight: 48, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'flex-start', 
                        gap: 2, 
                        paddingTop: 4, 
                        borderLeft: index === 0 ? 'none' : `1px solid ${calendarChrome.divider}`, 
                        background: isToday ? calendarChrome.todayBg : calendarChrome.header, 
                        color: isToday ? calendarChrome.todayText : calendarChrome.text, 
                        fontSize: 18, 
                        lineHeight: 1.1, 
                        position: 'relative',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: 14, marginBottom: 2 }}>{formatDate(day)}</div>
                      {contacts.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 46, overflowY: 'auto', padding: '0 4px 4px' }}>
                          {contacts.slice(0,4).map(c => (
                            <div key={c.id} title={c.name} style={{ background: isToday ? '#fff' : calendarChrome.contactBg, color: isToday ? calendarChrome.todayText : '#fff', borderRadius: 4, padding: '2px 4px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                              {c.name}
                            </div>
                          ))}
                          {contacts.length > 4 && <div style={{ fontSize: 10, opacity: 0.8 }}>+{contacts.length - 4} {t('calendar.more')}</div>}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <div id="calendarScrollbarSpacer" style={{ width: 0, flexShrink: 0, height: 48 }} />
            </div>
            )}
            {/* All-day appointments row */}
            <div className="calendar-grid-row" style={{ display: 'flex', background: calendarChrome.allDayBg, borderBottom: `1px solid ${calendarChrome.allDayBorder}`, flexShrink: 0 }}>
              <div style={{ width: gutterWidth, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px 0', textAlign: 'center' }}>
                <span style={{ fontSize: 9, color: calendarChrome.muted, textTransform: 'uppercase', letterSpacing: isPhone ? 0 : 1 }}>{t('calendar.allDay')}</span>
              </div>
              {(view === 'day' ? [currentDate] : weekDays).map((day, index) => {
                const dayAppts = getAllDayAppointmentsForDay(day);
                return (
                  <div className="calendar-grid-row"
                    key={index}
                    style={{ flex: 1, borderLeft: index === 0 ? 'none' : `1px solid ${calendarChrome.allDayBorder}`, padding: '3px 4px', minHeight: 28, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                    onDrop={(e) => {
                      e.preventDefault();
                      notifyDragEnd();
                      const raw = e.dataTransfer.getData('application/json');
                      if (!raw) return;
                      try {
                        const data = JSON.parse(raw);
                        if (data.type === 'appointment') handleMoveToAllDay(data.id, day);
                      } catch {}
                    }}
                  >
                    {dayAppts.map(appt => {
                      const patient = patients.find(p => p.id === appt.patientId);
                      const label = patient?.name ?? appt.prospectName ?? 'Patient';
                      const linkedTreatmentNames = Array.isArray(appt.treatments)
                        ? appt.treatments.map(treatment => treatment?.name).filter(Boolean)
                        : [];
                      const treatLabel = linkedTreatmentNames.length > 0
                        ? linkedTreatmentNames.join(', ')
                        : appt.treatment?.name || appt.treatmentTemplate?.name || appt.customReason;
                      const chipColor = appt.color ?? '#8b5cf6';
                      return (
                        <span
                          key={appt.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('application/json', JSON.stringify({
                              type: 'appointment',
                              id: appt.id,
                              durationMs: 60 * 60 * 1000
                            }));
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onClick={(e) => { e.stopPropagation(); handleAppointmentClick(appt, e); }}
                          title={treatLabel ? `${label} · ${treatLabel}` : label}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 2,
                            background: `${chipColor}22`, border: `1px solid ${chipColor}`,
                            borderRadius: 4, padding: '1px 6px', fontSize: 11,
                            color: chipColor, maxWidth: 160, cursor: 'grab',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                          }}
                        >
                          {label}{treatLabel ? ` · ${treatLabel}` : ''}
                        </span>
                      );
                    })}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAllDayClick(day); }}
                      title="Add all-day appointment"
                      style={{ background: 'none', border: 'none', color: calendarChrome.muted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px', flexShrink: 0 }}
                    >+</button>
                  </div>
                );
              })}
              <div id="allDayScrollbarSpacer" style={{ width: 0, flexShrink: 0 }} />
            </div>
            <div className="calendar-grid-row" id="calendarScroll" style={{ display: 'flex', flex: 1, overflowY: 'auto', background: calendarChrome.grid, alignItems: 'stretch' }}>
              <TimeGutter timeSlots={timeSlots} HOUR_HEIGHT={HOUR_HEIGHT} hoursRangeForWeek={hoursRangeForWeek} nowTick={nowTick} width={gutterWidth} />
              <div key={isPhone ? weekDays[0]?.toDateString() : undefined} className={slideClass ? `calendar-grid-row ${slideClass}` : 'calendar-grid-row'} style={{ display: 'flex', flex: 1, position: 'relative', minHeight: '100%' }}>
                {(() => { const now = new Date(nowTick); const hour = now.getHours() + now.getMinutes() / 60; const within = hour >= hoursRangeForWeek.start && hour < hoursRangeForWeek.end; const inThisWeek = weekDays.some(d => d.toDateString() === now.toDateString()); if (!within || !inThisWeek) return null; const nowTop = (hour - hoursRangeForWeek.start) * HOUR_HEIGHT; return (<div style={{ position: 'absolute', top: nowTop, left: 0, right: 0, height: 2, background: '#ff4d4f', zIndex: 25 }} />); })()}
                {view === 'day' && !isPhone ? (
                  [1, 2, 3, 4, 5].map((room, index) => (
                    <DayColumn
                      key={room}
                      day={currentDate}
                      index={index}
                      room={room}
                      HOUR_HEIGHT={HOUR_HEIGHT}
                      hoursRangeForWeek={hoursRangeForWeek}
                      timeSlots={timeSlots}
                      appointments={getAppointmentsForDay(currentDate).filter(a => a.room === room || (room === 1 && !a.room))}
                      events={getEventsForDay(currentDate)}
                      patients={augmentedPatients}
                      doctors={doctors}
                      nowTick={nowTick}
                      onTimeSlotClick={handleTimeSlotClick}
                      onAppointmentClick={handleAppointmentClick}
                      onEventClick={handleEventClick}
                      onMoveAppointment={onMoveAppointment}
                      onMoveEvent={onMoveEvent}
                      eventRailWidth={isPhone ? 8 : showMini ? 14 : 24}
                      onResizeStart={handleResizeStart}
                      onResize={handleResize}
                      onResizeEnd={handleResizeEnd}
                      onEventResizeStart={handleEventResizeStart}
                      onEventResize={handleEventResize}
                      onEventResizeEnd={handleEventResizeEnd}
                      getProspectEvent={(id) => eventLookup.get(id)}
                      filterDoctorId={filterDoctorId}
                      filterDoctorId2={filterDoctorId2}
                      filterRoom={filterRoom}
                      onAppointmentContextMenu={handleAppointmentContextMenu}
                      onEventContextMenu={handleEventContextMenu}
                    />
                  ))
                ) : (
                  weekDays.map((day, index) => (
                    <DayColumn
                      key={index}
                      day={day}
                      index={index}
                      HOUR_HEIGHT={HOUR_HEIGHT}
                      hoursRangeForWeek={hoursRangeForWeek}
                      timeSlots={timeSlots}
                      appointments={getAppointmentsForDay(day)}
                      events={getEventsForDay(day)}
                      patients={augmentedPatients}
                      doctors={doctors}
                      nowTick={nowTick}
                      onTimeSlotClick={isPhone
                        ? (slotDay, time, room) => handleTimeSlotClick(slotDay, time, room ?? (filterRoom ? Number(filterRoom) : undefined))
                        : handleTimeSlotClick}
                      onAppointmentClick={handleAppointmentClick}
                      onEventClick={handleEventClick}
                      onMoveAppointment={onMoveAppointment}
                      onMoveEvent={onMoveEvent}
                      eventRailWidth={isPhone ? 8 : showMini ? 14 : 24}
                      onResizeStart={handleResizeStart}
                      onResize={handleResize}
                      onResizeEnd={handleResizeEnd}
                      onEventResizeStart={handleEventResizeStart}
                      onEventResize={handleEventResize}
                      onEventResizeEnd={handleEventResizeEnd}
                      getProspectEvent={(id) => eventLookup.get(id)}
                      filterDoctorId={filterDoctorId}
                      filterDoctorId2={filterDoctorId2}
                      filterRoom={filterRoom}
                      onAppointmentContextMenu={handleAppointmentContextMenu}
                      onEventContextMenu={handleEventContextMenu}
                    />
                  ))
                )}
              </div>
            </div>
            </>
            )}
            {!isPhone && <StandByArea 
              appointments={undatedAppointments}
              patients={patients}
              doctors={doctors}
              onAppointmentClick={handleAppointmentClick}
              onMoveAppointment={onMoveAppointment}
            />}
          </div>
          )}
        </div>
      </div>

      {isPhone && canEditAppointments && !showModal && !showEventModal && !phoneSheetOpen && !phoneSearchOpen && (
        <PhoneFab onNewAppointment={phoneNewAppointment} onNewBlock={handleNewEvent} />
      )}
      {isPhone && (
        <PhoneCalendarSheet
          open={phoneSheetOpen}
          view={view}
          onClose={() => setPhoneSheetOpen(false)}
          onViewChange={nextView => { setSlideDirection(null); handleViewChange(nextView); }}
        >
          <CalendarToolbar
            {...toolbarProps}
            variant="sheet"
            onNewEvent={() => { setPhoneSheetOpen(false); handleNewEvent(); }}
          />
          {undatedAppointments.length > 0 && (
            <details className="calendar-options phone-cal-sheet-undated">
              <summary>{t('responsive.calendar.undated')} ({undatedAppointments.length})</summary>
              <StandByArea
                appointments={undatedAppointments}
                patients={patients}
                doctors={doctors}
                onAppointmentClick={(app, event) => { setPhoneSheetOpen(false); handleAppointmentClick(app, event); }}
                onMoveAppointment={onMoveAppointment}
              />
            </details>
          )}
        </PhoneCalendarSheet>
      )}

      <AppointmentModal
        open={showModal}
        onClose={closeAppointmentModal}
        onSubmit={handleSubmitAppointment}
        onDelete={handleDeleteAppointment}
        form={form}
        setForm={setForm}
        editingAppointment={editingAppointment}
        patients={patients}
        doctors={visibleDoctors}
        patientTreatments={patientTreatments}
        treatmentsLoading={treatmentsLoading}
        treatmentsError={patientTreatmentsError}
        treatmentTemplates={treatmentTemplates}
        laboratories={laboratories}
        treatmentTemplatesLoading={treatmentTemplatesLoading}
        treatmentTemplatesError={treatmentTemplatesError}
        onSaveDraft={async () => { await saveEditingAppointmentDraft(); }}
        onReservationFeePayment={handleReservationFeePayment}
        onReservationFeeUpdate={handleReservationFeeUpdate}
        onReservationFeeRemove={handleReservationFeeRemove}
        onReservationFeeRefund={handleReservationFeeRefund}
        canEditPayments={canEditPayments}
        canViewPayments={canViewPayments}
        canEditAppointments={canEditAppointments}
        onStatusChanged={handleAppointmentStatusChanged}
        onOpenPatientProfile={onOpenPatientProfile}
        onOpenTreatmentWorkspace={editingAppointment && onOpenTreatmentWorkspace
          ? (treatmentId, patientId) => onOpenTreatmentWorkspace(treatmentId, patientId, editingAppointment.id)
          : undefined}
        token={token}
        onRefreshAppointment={refreshEditedAppointment}
      />

      <EventModal
        open={showEventModal}
        onClose={closeEventModal}
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            const startDateTime = parseLaPazDateTime(eventForm.date, eventForm.startTime);
            const endDateTime = parseLaPazDateTime(eventForm.date, eventForm.endTime);
            if (!startDateTime || !endDateTime) throw new Error('Invalid event date/time');
            const payload: Record<string, any> = { 
              doctorId: eventForm.doctorId ? parseInt(eventForm.doctorId) : null, 
              startTime: startDateTime.toISOString(), 
              endTime: endDateTime.toISOString(), 
              title: eventForm.title, 
              notes: eventForm.notes,
              recurrenceRule: eventForm.recurrenceRule || null,
              recurrenceEndDate: eventForm.recurrenceEndDate ? parseLaPazDateTime(eventForm.recurrenceEndDate, '00:00')?.toISOString() ?? null : null
            };
            if (editingEvent?.isNewPatientIntake) {
              payload.isNewPatientIntake = true;
              payload.prospectName = editingEvent.prospectName ?? null;
              payload.prospectContact = editingEvent.prospectContact ?? null;
              payload.prospectNotes = editingEvent.prospectNotes ?? null;
            }
            if (editingEvent) { 
              setHistory(prev => [...prev, { type: 'EDIT_EVENT', id: editingEvent.id, previousData: editingEvent }]);
              const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.EVENTS}/${editingEvent.id}`), { method: 'PUT', headers: getAuthHeaders(token), body: JSON.stringify(payload) }); 
              if (!res.ok) throw new Error('Failed to update event'); 
              upsertEvent({ ...editingEvent, ...payload, id: editingEvent.id } as CalendarEvent);
            }
            else { 
              const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.EVENTS), { method: 'POST', headers: getAuthHeaders(token), body: JSON.stringify(payload) }); 
              if (!res.ok) throw new Error('Failed to create event'); 
              const created = await res.json();
              upsertEvent(created);
              setHistory(prev => [...prev, { type: 'CREATE_EVENT', id: created.id }]);
            }
            closeEventModal();
          } catch (err) { console.error('Failed to save event:', err); }
        }}
        onDelete={async () => { 
          if (!editingEvent || !window.confirm(t('calendar.errors.deleteConfirm'))) return; 
          setHistory(prev => [...prev, { type: 'DELETE_EVENT', data: editingEvent }]);
          try { 
            const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.EVENTS}/${editingEvent.id}`), { method: 'DELETE', headers: getAuthHeaders(token) }); 
            if (!res.ok) throw new Error('Failed to delete event'); 
            setEvents(prev => prev.filter(event => event.id !== editingEvent.id));
            setEventsCached(true);
            closeEventModal(); 
          } catch (err) { console.error('Failed to delete event:', err); } 
        }}
        form={eventForm}
        setForm={(f) => setEventForm(f)}
        editingEvent={editingEvent}
        doctors={visibleDoctors}
        onConvertIntake={handleConvertIntake}
      />

      {contextMenu && (
        <CalendarContextMenu
          menu={contextMenu}
          doctors={visibleDoctors}
          hasWorkspaceShortcut={(() => {
            const appointment = contextMenu.type === 'appointment'
              ? displayAppointments.find(candidate => candidate.id === contextMenu.itemId)
              : null;
            return !!(
              onOpenTreatmentWorkspace
              && appointment
              && typeof appointment.patientId === 'number'
              && appointment.patientId > 0
              && (
                typeof appointment.treatmentId === 'number'
                || typeof appointment.treatment?.id === 'number'
              )
            );
          })()}
          onClose={closeContextMenu}
          onDoctorChange={handleQuickDoctorChange}
          onOpenTreatmentWorkspace={handleContextOpenTreatmentWorkspace}
          onDeleteAppointment={handleContextDeleteAppointment}
        />
      )}
    </div>
  );
}

